import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  listenToBrandProductsFeed,
  stopBrandProductsFeed,
  upsertProduct,
  upsertProductMedia,
  deleteProduct,
  deleteProductMedia,
  Product,
} from "../../../redux/storeProductSlice";
import {
  Plus, Search, X, ChevronLeft, ChevronDown, Image as ImageIcon,
  Trash2, Package, Check, Camera, RotateCcw, Wand2,
  Bold, Italic, Underline, Link2, ListOrdered, List as ListIcon,
  AlignLeft, Quote, Undo2, Redo2, GripHorizontal, Truck,
  Palette, Video, Tag, Layers, Scale, SlidersHorizontal,
} from "lucide-react";

import Countries from "../../../GLOBAL/Countries";
import { useNavigate } from "react-router-dom";
import { DEPARTMENTS, AllCategoriesFlat, type CategoryItem } from "../../../CATEGORIES/CategoriesCollection";
import {
  attributesForCategory,
  findNodePath,
} from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";
import {
  type RateType,
  DEFAULT_RATE_TYPE,
} from "../../../redux/storeProductServiceFeeSlice";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
   The screen paints nothing behind itself — it sits directly on
   whatever the dashboard shell already has. No wrapper, no card
   around the whole page.
═══════════════════════════════════════════════════════════ */
const HEART = {
  panelBg:   "#FFFFFF",
  panel2:    "#FFF9EF",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  green:     "#3F8A4C",
  greenSoft: "#E7F3E8",
  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

const SP_CSS = `
  .sp-root{
    font-family:'Montserrat','Cormorant Garamond',sans-serif;
    color:${HEART.dark};
    background:transparent;      /* inherit the page — no wrapper */
    min-height:0;
    overflow:visible;
    padding-bottom:40px;
  }
  .sp-root ::-webkit-scrollbar{width:8px;height:8px;}
  .sp-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .sp-root button{font-family:inherit;}
  .sp-root input,.sp-root textarea,.sp-root select{font-family:inherit;}

  @keyframes spPop{from{opacity:0;transform:translateY(8px) scale(.985);}to{opacity:1;transform:none;}}
  @keyframes spSlideIn{from{opacity:0;transform:translateX(12px);}to{opacity:1;transform:none;}}
  @keyframes spSpin{to{transform:rotate(360deg);}}
  @keyframes spShimmer{0%{background-position:-400px 0;}100%{background-position:400px 0;}}
  @keyframes spSheetUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
  @keyframes spFadeIn{from{opacity:0;}to{opacity:1;}}
  @keyframes spFloatIn{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:none;}}

  .sp-fade{animation:spPop .22s ease both;}
  .sp-slide{animation:spSlideIn .22s ease both;}
  .sp-spin{animation:spSpin .7s linear infinite;}
  .sp-skeleton{background:linear-gradient(90deg,${HEART.goldSoft} 0%,#fff 50%,${HEART.goldSoft} 100%);background-size:800px 100%;animation:spShimmer 1.4s linear infinite;}

  @media (prefers-reduced-motion: reduce){
    .sp-fade,.sp-slide,.sp-skeleton,.sp-spin{animation:none !important;}
  }

  .sp-num{font-variant-numeric:tabular-nums;font-weight:800;letter-spacing:-.01em;color:${HEART.redDark};}

  .sp-btn{border:none;border-radius:11px;font-weight:700;cursor:pointer;transition:transform .1s,box-shadow .15s,background .15s,border-color .15s,color .15s;}
  .sp-btn:active{transform:scale(.97);}
  .sp-btn:focus-visible{outline:2px solid ${HEART.gold};outline-offset:2px;}
  .sp-btn-primary{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});color:#fff;box-shadow:0 6px 16px rgba(200,32,46,.28);}
  .sp-btn-primary:hover{box-shadow:0 8px 20px rgba(200,32,46,.36);}
  .sp-btn-primary:disabled{opacity:.55;cursor:not-allowed;box-shadow:none;}
  .sp-btn-ghost{background:#fff;color:${HEART.dark};border:1px solid ${HEART.border};}
  .sp-btn-ghost:hover{background:${HEART.goldSoft};border-color:${HEART.gold};}
  .sp-btn-danger{background:${HEART.redSoft};color:${HEART.redDark};border:1px solid ${HEART.redSoft};}
  .sp-btn-danger:hover{background:#f6cfd2;}

  .sp-input,.sp-textarea,.sp-select{
    width:100%;border:1px solid ${HEART.border};border-radius:12px;background:#fff;
    padding:11px 13px;font-size:13.5px;color:${HEART.dark};outline:none;transition:border-color .15s,box-shadow .15s;
  }
  .sp-input:focus,.sp-textarea:focus,.sp-select:focus{border-color:${HEART.gold};box-shadow:0 0 0 3px ${HEART.goldSoft};}
  .sp-input::placeholder{color:${HEART.textFaint};}
  .sp-label{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:${HEART.textMuted};margin-bottom:7px;display:block;}
  .sp-hint{font-size:11.5px;color:${HEART.textFaint};margin-top:6px;}

  .sp-card{background:#fff;border:1px solid ${HEART.border};border-radius:18px;padding:20px;box-shadow:0 3px 12px rgba(27,14,6,.05);}
  .sp-cardtitle{font-size:15px;font-weight:800;color:${HEART.dark};margin-bottom:14px;}
  .sp-cardhead{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .sp-cardic{width:30px;height:30px;border-radius:9px;background:${HEART.goldSoft};color:${HEART.goldDark};display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .sp-cardic.hot{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});color:#fff;box-shadow:0 3px 8px rgba(200,32,46,.3);}

  .sp-toggle{position:relative;width:38px;height:22px;border-radius:999px;border:none;cursor:pointer;flex-shrink:0;transition:background .15s;background:${HEART.border};}
  .sp-toggle.on{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});}
  .sp-toggle span{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform .15s;box-shadow:0 1px 3px rgba(0,0,0,.25);}
  .sp-toggle.on span{transform:translateX(16px);}

  .sp-pill{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:800;padding:5px 10px;border-radius:999px;letter-spacing:.03em;}
  .sp-pill-active{background:${HEART.greenSoft};color:${HEART.green};}
  .sp-pill-draft{background:${HEART.goldSoft};color:${HEART.goldDark};}
  .sp-pill-oos{background:${HEART.redSoft};color:${HEART.redDark};}

  .sp-chips{display:flex;flex-wrap:wrap;gap:8px;}
  .sp-chip{display:inline-flex;align-items:center;gap:6px;background:${HEART.panel2};border:1px solid ${HEART.border};color:${HEART.dark};font-size:12px;font-weight:700;padding:7px 13px;border-radius:999px;cursor:pointer;transition:background .12s,border-color .12s,color .12s,box-shadow .12s;}
  .sp-chip:hover{border-color:${HEART.gold};background:${HEART.goldSoft};}
  .sp-chip.on{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});color:#fff;border-color:transparent;box-shadow:0 3px 10px rgba(200,32,46,.26);}
  .sp-chip-del{display:inline-flex;background:none;border:none;padding:0;cursor:pointer;color:inherit;opacity:.7;}
  .sp-chip-del:hover{opacity:1;}

  .sp-searchbar{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid ${HEART.border};border-radius:999px;padding:9px 15px;}
  .sp-searchbar input{border:none;outline:none;background:transparent;font-size:13px;flex:1;min-width:0;color:${HEART.dark};}

  /* ── topbar ── */
  .sp-topbar{background:#fff;border:1px solid ${HEART.border};border-radius:18px;box-shadow:0 3px 12px rgba(27,14,6,.05);overflow:hidden;margin-bottom:18px;}
  .sp-topbar-a{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px;border-bottom:1px solid ${HEART.divider};flex-wrap:wrap;}
  .sp-topbar-tabs{display:flex;gap:20px;}
  .sp-topbar-tab{background:none;border:none;padding:2px 0;font-size:13px;font-weight:800;color:${HEART.textFaint};cursor:pointer;position:relative;display:flex;align-items:center;gap:6px;}
  .sp-topbar-tab.active{color:${HEART.dark};}
  .sp-topbar-tab.active::after{content:"";position:absolute;left:0;right:0;bottom:-14px;height:2px;background:${HEART.red};border-radius:2px;}
  .sp-topbar-b{display:flex;align-items:center;gap:12px;padding:12px 16px;}

  /* ── table ── */
  .sp-ptable{background:#fff;border:1px solid ${HEART.border};border-radius:18px;box-shadow:0 3px 12px rgba(27,14,6,.05);overflow:hidden;}
  .sp-pt-scroll{overflow-x:auto;}
  .sp-pt-grid{min-width:980px;}
  .sp-pt-head,.sp-pt-row{display:grid;grid-template-columns:44px 2.2fr 1fr 1fr 1fr 1.2fr .9fr 1.1fr 1.1fr;align-items:center;gap:12px;padding:0 16px;}
  .sp-pt-head{height:44px;background:${HEART.panel2};border-bottom:1px solid ${HEART.border};}
  .sp-pt-hcell{font-size:10px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:${HEART.textFaint};white-space:nowrap;}
  .sp-pt-row{min-height:62px;border-bottom:1px solid ${HEART.divider};cursor:pointer;transition:background .1s;}
  .sp-pt-row:last-child{border-bottom:none;}
  .sp-pt-row:hover{background:${HEART.panel2};}
  .sp-pt-row.selected{background:${HEART.redSoft};}
  .sp-pt-prod{display:flex;align-items:center;gap:11px;min-width:0;}
  .sp-pt-thumb{width:42px;height:42px;border-radius:11px;overflow:hidden;flex-shrink:0;background:${HEART.panel2};border:1px solid ${HEART.border};display:flex;align-items:center;justify-content:center;}
  .sp-pt-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
  .sp-pt-name{font-size:13px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .sp-pt-sub{font-size:10.5px;font-weight:500;color:${HEART.textFaint};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .sp-pt-cell{font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .sp-pt-cell.muted{color:${HEART.textFaint};font-weight:500;}
  .sp-pt-cell.oos{color:${HEART.redDark};font-weight:700;}
  .sp-dots{display:flex;align-items:center;gap:3px;}
  .sp-dot{width:13px;height:13px;border-radius:50%;border:1px solid rgba(27,14,6,.14);flex-shrink:0;}

  .sp-pcard-check{width:20px;height:20px;border-radius:6px;border:2px solid ${HEART.border};background:#fff;flex-shrink:0;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:border-color .15s,background .15s;color:#fff;}
  .sp-pcard-check.on{background:${HEART.red};border-color:${HEART.red};}

  /* ── floating bulk bar ── */
  .sp-bulkfloat{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:120;display:flex;align-items:center;gap:10px;background:${HEART.dark};color:#fff;padding:12px 18px;border-radius:16px;box-shadow:0 16px 40px rgba(27,14,6,.35);flex-wrap:wrap;justify-content:center;max-width:94vw;}
  .sp-bulkfloat-btn{background:rgba(255,255,255,.12);border:none;color:#fff;font-size:12px;font-weight:700;padding:8px 14px;border-radius:10px;cursor:pointer;transition:background .15s;}
  .sp-bulkfloat-btn:hover{background:rgba(255,255,255,.22);}
  .sp-bulkfloat-btn.danger{background:${HEART.red};}
  .sp-bulkfloat-btn.danger:hover{background:${HEART.redDark};}

  /* ── media ── */
  .sp-media-drop{border:1.5px dashed ${HEART.border};border-radius:16px;background:${HEART.panel2};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:34px 16px;cursor:pointer;transition:border-color .15s,background .15s;text-align:center;}
  .sp-media-drop:hover{border-color:${HEART.gold};background:${HEART.goldSoft};}
  .sp-media-drop.dragging{border-color:${HEART.red};background:${HEART.redSoft};}
  .sp-thumb{position:relative;width:100%;aspect-ratio:1;border-radius:13px;overflow:hidden;border:1px solid ${HEART.border};background:${HEART.panel2};}
  .sp-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
  .sp-thumb-del{position:absolute;top:6px;right:6px;width:23px;height:23px;border-radius:50%;background:rgba(27,14,6,.68);border:none;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;opacity:0;transition:opacity .12s;}
  .sp-thumb:hover .sp-thumb-del,.sp-thumb:focus-within .sp-thumb-del{opacity:1;}
  .sp-thumb-tag{position:absolute;left:6px;bottom:6px;font-size:9px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:999px;color:#fff;}

  .sp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:70px 20px;text-align:center;}
  .sp-section-divider{height:1px;background:${HEART.divider};margin:20px 0;border:none;}

  /* ── searchable picker: trigger + panel ── */
  .sp-trigger{width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid ${HEART.border};border-radius:12px;background:#fff;padding:11px 13px;font-size:13.5px;color:${HEART.dark};cursor:pointer;text-align:left;transition:border-color .15s,box-shadow .15s;}
  .sp-trigger:hover{border-color:${HEART.gold};}
  .sp-trigger.open{border-color:${HEART.gold};box-shadow:0 0 0 3px ${HEART.goldSoft};}
  .sp-trigger-val{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;}
  .sp-trigger-ph{color:${HEART.textFaint};}

  .sp-pop{position:fixed;z-index:280;background:#fff;border:1px solid ${HEART.border};border-radius:16px;box-shadow:0 20px 48px rgba(27,14,6,.18);display:flex;flex-direction:column;overflow:hidden;animation:spFloatIn .16s ease both;}
  .sp-pop-head{padding:12px 12px 8px;border-bottom:1px solid ${HEART.divider};background:${HEART.panel2};}
  .sp-pop-title{display:flex;align-items:center;justify-content:space-between;margin-bottom:9px;}
  .sp-pop-title p{font-size:12.5px;font-weight:800;margin:0;}
  .sp-pop-list{overflow-y:auto;padding:6px;}
  .sp-opt{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;background:transparent;border:none;border-radius:10px;padding:10px 11px;cursor:pointer;text-align:left;font-size:13px;font-weight:600;color:${HEART.dark};transition:background .12s;}
  .sp-opt:hover{background:${HEART.panel2};}
  .sp-opt.on{background:${HEART.redSoft};color:${HEART.redDark};font-weight:800;}
  .sp-pop-foot{padding:10px 12px;border-top:1px solid ${HEART.divider};display:flex;gap:8px;}

  /* ── colour spheres ── */
  .sp-colorgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(70px,1fr));gap:12px;max-height:290px;overflow-y:auto;padding:4px 2px;}
  .sp-colorcell{display:flex;flex-direction:column;align-items:center;gap:6px;background:none;border:none;padding:0;cursor:pointer;}
  .sp-sphere{width:40px;height:40px;border-radius:50%;display:block;transition:transform .18s;}
  .sp-colorcell:hover .sp-sphere{transform:scale(1.12) translateY(-2px);}
  .sp-sphere.on{outline:2.5px solid ${HEART.red};outline-offset:3px;}
  .sp-spheretick{position:absolute;bottom:-3px;right:-3px;width:16px;height:16px;border-radius:50%;background:${HEART.red};display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.3);}
  .sp-colorname{font-size:10px;font-weight:600;color:${HEART.textMuted};text-align:center;line-height:1.2;max-width:68px;word-break:break-word;}
  .sp-colorcell.on .sp-colorname{color:${HEART.redDark};font-weight:800;}

  /* ── variant cards ── */
  .sp-variant{border:1px solid ${HEART.border};border-radius:15px;background:#fff;padding:13px;display:flex;gap:13px;transition:border-color .15s,box-shadow .15s;}
  .sp-variant:hover{border-color:rgba(200,32,46,.28);box-shadow:0 4px 14px rgba(200,32,46,.08);}
  .sp-variant + .sp-variant{margin-top:10px;}
  .sp-varshot{width:88px;height:104px;border-radius:12px;border:1.5px dashed ${HEART.border};background:${HEART.panel2};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;cursor:pointer;overflow:hidden;flex-shrink:0;position:relative;transition:border-color .15s;}
  .sp-varshot:hover{border-color:${HEART.red};background:${HEART.redSoft};}
  .sp-varshot img{width:100%;height:100%;object-fit:cover;display:block;}
  .sp-varhead{display:flex;align-items:center;gap:8px;margin-bottom:11px;}
  .sp-varswatch{width:15px;height:15px;border-radius:50%;border:1px solid rgba(27,14,6,.14);flex-shrink:0;}
  .sp-varname{font-size:11px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;}
  .sp-varsku{font-size:10px;font-weight:600;color:${HEART.textFaint};font-family:ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:.02em;}
  .sp-inherit{display:inline-flex;align-items:center;gap:4px;font-size:9.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:${HEART.textFaint};background:${HEART.panel2};border:1px solid ${HEART.border};border-radius:999px;padding:3px 8px;cursor:pointer;}
  .sp-inherit:hover{border-color:${HEART.red};color:${HEART.redDark};}

  .sp-mini{border:1px solid ${HEART.border};border-radius:9px;background:#fff;padding:8px 10px;font-size:12.5px;color:${HEART.dark};outline:none;width:100%;min-width:0;transition:border-color .15s;}
  .sp-mini:focus{border-color:${HEART.gold};}

  /* ── expandable summary row (mobile) ── */
  .sp-openrow{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;background:#fff;border:1px solid ${HEART.border};border-radius:18px;padding:18px 20px;box-shadow:0 3px 12px rgba(27,14,6,.05);cursor:pointer;text-align:left;}
  .sp-openrow:hover{border-color:${HEART.gold};}
  .sp-openrow-l{display:flex;align-items:center;gap:12px;min-width:0;}
  .sp-openrow-sum{font-size:12px;color:${HEART.textMuted};margin:3px 0 0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .sp-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 6px;border-radius:999px;background:${HEART.red};color:#fff;font-size:10px;font-weight:800;}

  /* ── attributes ── */
  .sp-attr-field{background:${HEART.panel2};border:1px solid ${HEART.divider};border-radius:14px;padding:13px 14px;transition:border-color .15s;}
  .sp-attr-field.wide{grid-column:1 / -1;}
  .sp-attr-field:focus-within{border-color:${HEART.gold};}
  .sp-attr-field .sp-trigger,.sp-attr-field .sp-input{background:#fff;}
  .sp-attr-req{color:${HEART.red};margin-left:3px;}

  /* ── rich text editor ── */
  .sp-editor{border:1px solid ${HEART.border};border-radius:12px;overflow:hidden;background:#fff;transition:border-color .15s,box-shadow .15s;}
  .sp-editor.focused{border-color:${HEART.gold};box-shadow:0 0 0 3px ${HEART.goldSoft};}
  .sp-editor-toolbar{display:flex;align-items:center;gap:2px;flex-wrap:wrap;padding:7px 8px;border-bottom:1px solid ${HEART.divider};background:${HEART.panel2};}
  .sp-etool{display:flex;align-items:center;gap:3px;background:transparent;border:none;border-radius:7px;padding:6px 7px;cursor:pointer;color:${HEART.textMuted};transition:background .12s,color .12s;}
  .sp-etool:hover{background:${HEART.goldSoft};color:${HEART.dark};}
  .sp-etool.active{background:${HEART.gold};color:#fff;}
  .sp-etool-select{display:flex;align-items:center;gap:4px;background:transparent;border:none;border-radius:7px;padding:6px 8px;cursor:pointer;color:${HEART.dark};font-size:12.5px;font-weight:700;}
  .sp-etool-select:hover{background:${HEART.goldSoft};}
  .sp-etool-sep{width:1px;height:20px;background:${HEART.border};margin:0 4px;}
  .sp-editor-body{width:100%;min-height:150px;max-height:340px;overflow-y:auto;padding:13px 14px;font-size:13.5px;line-height:1.65;outline:none;}
  .sp-editor-body:empty::before{content:attr(data-placeholder);color:${HEART.textFaint};}
  .sp-editor-body h1{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;margin:0 0 10px;}
  .sp-editor-body h2{font-family:'Cormorant Garamond',serif;font-size:19px;font-weight:700;margin:0 0 8px;}
  .sp-editor-body h3{font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700;margin:0 0 6px;}
  .sp-editor-body p{margin:0 0 10px;}
  .sp-editor-body ul,.sp-editor-body ol{margin:0 0 10px;padding-left:20px;}
  .sp-editor-body blockquote{margin:0 0 10px;padding:6px 0 6px 12px;border-left:3px solid ${HEART.gold};color:${HEART.textMuted};}
  .sp-editor-body a{color:${HEART.redDark};text-decoration:underline;}
  .sp-editor-body img,.sp-editor-body iframe,.sp-editor-body video{max-width:100%;border-radius:10px;}
  .sp-color-pop{position:absolute;top:calc(100% + 6px);left:0;background:#fff;border:1px solid ${HEART.border};border-radius:14px;box-shadow:0 12px 28px rgba(27,14,6,.16);padding:12px;z-index:30;}
  .sp-color-pop-label{font-size:10.5px;font-weight:800;color:${HEART.textFaint};margin:0 0 8px;text-transform:uppercase;letter-spacing:.05em;}
  .sp-color-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:7px;}
  .sp-color-swatch{width:24px;height:24px;border-radius:7px;border:1px solid rgba(27,14,6,.08);cursor:pointer;padding:0;transition:transform .1s;}
  .sp-color-swatch:hover{transform:scale(1.12);}

  /* ── rate type: two selectable cards, ShortForm7 style ── */
  .sp-rategrid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .sp-ratecard{display:flex;align-items:flex-start;gap:10px;text-align:left;border:2px solid ${HEART.border};border-radius:14px;background:#fff;padding:13px;cursor:pointer;transition:border-color .15s,background .15s,box-shadow .15s;}
  .sp-ratecard:hover{border-color:${HEART.gold};}
  .sp-ratecard.on{border-color:${HEART.red};background:${HEART.redSoft};box-shadow:0 4px 16px rgba(200,32,46,.16);}
  .sp-ratecard-ic{width:30px;height:30px;border-radius:9px;background:${HEART.goldSoft};color:${HEART.goldDark};display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s,color .15s;}
  .sp-ratecard.on .sp-ratecard-ic{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});color:#fff;}
  .sp-ratecard-t{font-size:13px;font-weight:800;margin:0 0 2px;}
  .sp-ratecard-b{font-size:11px;color:${HEART.textMuted};margin:0;line-height:1.45;}
  .sp-bigrate{display:flex;align-items:baseline;justify-content:center;gap:8px;padding:14px 0 4px;}
  .sp-bigrate-n{font-size:38px;font-weight:900;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:${HEART.dark};}
  .sp-bigrate-u{font-size:14px;font-weight:800;color:${HEART.redDark};}
  @media(max-width:520px){ .sp-rategrid{grid-template-columns:1fr;} }

  /* ── overlays ── */
  .sp-sheet-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.4);z-index:250;animation:spFadeIn .16s ease both;}
  .sp-sheet{position:fixed;left:0;right:0;bottom:0;z-index:260;background:#fff;border-radius:22px 22px 0 0;box-shadow:0 -12px 40px rgba(27,14,6,.28);max-height:88vh;display:flex;flex-direction:column;animation:spSheetUp .22s cubic-bezier(.32,.72,0,1) both;color:${HEART.dark};}
  .sp-sheet-handle{display:flex;justify-content:center;padding:10px 0 4px;}
  .sp-sheet-header{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:2px 18px 12px;}
  .sp-sheet-body{overflow-y:auto;padding:6px 18px 28px;}
  .sp-modal{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:280;background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(27,14,6,.32);width:380px;max-width:92vw;padding:22px;animation:spPop .18s ease both;}
  .sp-modal h3{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:700;margin:0 0 14px;}
  .sp-modal-actions{display:flex;gap:10px;margin-top:18px;}
  .sp-bigmodal{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:260;background:#fff;border-radius:22px;box-shadow:0 24px 60px rgba(27,14,6,.32);width:440px;max-width:92vw;max-height:86vh;display:flex;flex-direction:column;overflow:hidden;animation:spPop .2s ease both;}

  /* ── category picker rows ── */
  .sp-cat-crumbs{display:flex;align-items:center;flex-wrap:wrap;gap:5px;padding:0 18px 9px;font-size:12px;}
  .sp-cat-crumb{background:${HEART.goldSoft};border:1px solid ${HEART.border};color:${HEART.goldDark};font-weight:700;padding:4px 10px;border-radius:999px;cursor:pointer;font-size:11px;}
  .sp-cat-crumb:hover{background:${HEART.gold};color:#fff;}
  .sp-cat-crumb-sep{color:${HEART.textFaint};}
  .sp-cat-list{overflow-y:auto;padding:4px 12px 14px;}
  .sp-cat-row{display:flex;align-items:center;gap:11px;width:100%;border-radius:13px;padding:12px;transition:background .12s;}
  .sp-cat-row:hover{background:${HEART.panel2};}
  .sp-cat-row.sel{background:${HEART.redSoft};}
  .sp-cat-ic{width:34px;height:34px;border-radius:10px;background:${HEART.goldSoft};color:${HEART.goldDark};display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;}
  .sp-cat-name{flex:1;font-size:13.5px;font-weight:700;min-width:0;}
  .sp-cat-pick{font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:${HEART.red};border:1px solid ${HEART.red};background:#fff;padding:5px 10px;border-radius:999px;cursor:pointer;flex-shrink:0;}
  .sp-cat-pick:hover{background:${HEART.red};color:#fff;}

  /* ── country sphere ── */
  .sp-sphere-wrap{position:relative;width:100%;height:250px;margin-top:8px;perspective:900px;cursor:grab;touch-action:none;overflow:hidden;}
  .sp-sphere-wrap:active{cursor:grabbing;}
  .sp-sphere-group{position:absolute;top:50%;left:50%;width:0;height:0;transform-style:preserve-3d;transform:rotateY(calc(var(--angle,0) * 1deg));}
  .sp-sphere-pt{position:absolute;top:0;left:0;transform-style:preserve-3d;}
  .sp-sphere-badge{display:flex;align-items:center;gap:5px;transform:translate(-50%,-50%) rotateY(calc(var(--angle,0) * -1deg));padding:5px 10px;border-radius:999px;background:#fff;border:1px solid ${HEART.border};font-size:11px;font-weight:800;white-space:nowrap;cursor:pointer;box-shadow:0 2px 6px rgba(27,14,6,.08);transition:background .12s,box-shadow .12s;}
  .sp-sphere-badge:hover{background:${HEART.goldSoft};box-shadow:0 4px 10px rgba(27,14,6,.15);}
  .sp-sphere-badge.selected{background:linear-gradient(135deg,${HEART.red},${HEART.redDark});color:#fff;border-color:transparent;}

  .sp-formhead{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;padding:2px 0 14px;margin-bottom:18px;border-bottom:1px solid ${HEART.divider};}
  .sp-sticky-save{position:fixed;left:0;right:0;bottom:0;z-index:110;padding:12px 16px calc(12px + env(safe-area-inset-bottom));background:#fff;border-top:1px solid ${HEART.border};box-shadow:0 -6px 20px rgba(27,14,6,.08);display:flex;gap:10px;}

  @media(max-width:900px){
    .sp-bigmodal{display:none !important;}
    .sp-formgrid{grid-template-columns:1fr !important;}
    .sp-mediagrid{grid-template-columns:repeat(3,1fr) !important;}
    .sp-variant{flex-direction:column;}
    .sp-varshot{width:100%;height:140px;}
  }
`;

/* ═══════════════════════════════════════════════════════════
   CATEGORY + ATTRIBUTE LOOKUP
═══════════════════════════════════════════════════════════ */
const NODES = new Map<string, CategoryItem>(AllCategoriesFlat.map((c) => [c.id, c]));

const catPath = (id: string): CategoryItem[] => {
  const chain: CategoryItem[] = [];
  const seen = new Set<string>();
  let cur = NODES.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parentId ? NODES.get(cur.parentId) : undefined;
  }
  return chain;
};
const deptOf = (id: string) => catPath(id)[0]?.id ?? "";
const hasChildren = (id: string) => AllCategoriesFlat.some((c) => c.parentId === id);
const pathLabel = (id: string) => catPath(id).map((c) => c.name).join(" › ");

/** Collection ids are 2-digit per level ("09"); the attribute tree is 3-digit
    ("009"). Walk up the chain trying both widths until something answers. */
const resolveAttributes = (categoryId: string): { key: string; attrs: any[] } => {
  if (!categoryId) return { key: "", attrs: [] };
  const keys = [...catPath(categoryId)].reverse().flatMap((c) => [`0${c.id}`, c.id]);
  for (const key of keys) {
    try {
      const found = attributesForCategory(key as any);
      if (found && found.length) return { key, attrs: found };
    } catch {
      /* unknown key — keep walking up */
    }
  }
  return { key: "", attrs: [] };
};

const isColorAttr = (a: any) => a?.inputType === "color" || /colou?r/i.test(String(a?.name ?? ""));

/** Colour is injected into every category by the attribute collection. Milk
    doesn't come in colours, so keep it only where a category declares it —
    or where the department is one people actually shop by colour. */
const COLOURLESS_DEPTS = new Set(["07", "09", "13"]); // Farm, Food, Grocery

const declaresColor = (attrKey: string): boolean => {
  if (!attrKey) return false;
  try {
    const path = findNodePath(attrKey as any) || [];
    return path.some((n: any) => (n.attributes ?? []).some(isColorAttr));
  } catch {
    return false;
  }
};

/* ═══════════════════════════════════════════════════════════
   SKU
   Readable and unique: NAME-CAT-STAMP+RANDOM, checked against
   every SKU already in the brand before it's handed out.
═══════════════════════════════════════════════════════════ */
const skuPart = (s: string, n = 3) =>
  (s || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, n)
    .padEnd(n, "X");

const makeSku = (name: string, categoryName: string, taken: Set<string>): string => {
  const base = `${skuPart(name)}-${skuPart(categoryName)}`;
  for (let i = 0; i < 50; i++) {
    const stamp = Date.now().toString(36).slice(-3).toUpperCase();
    const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
    const sku = `${base}-${stamp}${rand}`;
    if (!taken.has(sku)) return sku;
  }
  return `${base}-${Math.random().toString(36).slice(2, 9).toUpperCase()}`;
};

const makeVariantSku = (baseSku: string, colorName: string, taken: Set<string>): string => {
  const root = `${baseSku || "SKU"}-${skuPart(colorName)}`;
  if (!taken.has(root)) return root;
  for (let i = 2; i < 99; i++) {
    const candidate = `${root}${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${root}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
};

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
type ViewMode = "list" | "form";

export interface Variant {
  colorId: string;
  name: string;
  hex?: string;
  imageUrl: string;
  previewUrl?: string;
  file?: File;
  stock: number;
  rate: number | null; // null → follows the base price
  sku: string;
}

const emptyForm = () => ({
  name: "",
  description: "",
  category: "",
  categoryId: "",
  vendor: "",
  tags: [] as string[],
  rate: 0,
  rateType: DEFAULT_RATE_TYPE as RateType, // perProduct — one price for the listing
  compareAtPrice: 0,
  costPerItem: 0,
  chargeTax: false,
  stock: 0,
  sku: "",
  barcode: "",
  sellWhenOutOfStock: false,
  weight: 0,
  originCountry: "",
  isPublished: false,
  isDraft: true,
  photos: [] as string[],
  attributes: {} as Record<string, any>,
  variants: {} as Record<string, Variant>,
});

type FormShape = ReturnType<typeof emptyForm>;

const fmtMoney = (n?: number) =>
  `€${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── the two ways a product is priced ───────────────────────
   perProduct — one price, whatever quantity is ordered
   perItem    — the price is for a single unit, so each colour
                can carry its own                              */
const RATE_LABEL: Record<RateType, string> = {
  perProduct: "/ product",
  perItem: "/ item",
};

const RATE_COPY: Record<RateType, { title: string; blurb: string }> = {
  perProduct: {
    title: "Per product",
    blurb: "One price for the listing, however many they take.",
  },
  perItem: {
    title: "Per item",
    blurb: "Priced by the unit — each colour can set its own.",
  },
};

/** Money with the unit it's charged by: "€12.00 / item". */
const fmtRate = (n: number | undefined, rateType?: RateType | null) =>
  `${fmtMoney(n)} ${RATE_LABEL[(rateType as RateType) ?? DEFAULT_RATE_TYPE] ?? RATE_LABEL.perProduct}`;

const sphere3D = (hex: string) =>
  `radial-gradient(circle at 35% 32%, rgba(255,255,255,.55) 0%, rgba(255,255,255,.10) 28%, transparent 52%),` +
  `radial-gradient(circle at 68% 72%, rgba(0,0,0,.30) 0%, transparent 55%),` +
  `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`;

const LIGHT_HEX = new Set([
  "#FFFFFF", "#FFFFF0", "#FFFDD0", "#FAFAFA", "#E6E6FA", "#F5F5DC", "#F8F8F0",
  "#87CEEB", "#98FF98", "#CDDC39", "#FFEB3B", "#FFC107", "#FFDB58", "#EAE0C8",
]);

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ═══════════════════════════════════════════════════════════
   NUMBER STEPPER
═══════════════════════════════════════════════════════════ */
const NumberStepper: React.FC<{
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  decimals?: boolean;
  prefix?: string;
  suffix?: string;
  compact?: boolean;
}> = ({ value, onChange, min = 0, max = 9999999, step = 1, decimals = false, prefix, suffix, compact }) => {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const bump = (d: number) => onChange(clamp(parseFloat(((value || 0) + d).toFixed(2))));
  const handle = (raw: string) => {
    let s = raw.replace(/[^0-9.]/g, "");
    if (!decimals) s = s.replace(/\./g, "");
    if (/^0\d/.test(s)) s = s.replace(/^0+/, "");
    if (s === "" || s === ".") return onChange(0);
    const n = decimals ? parseFloat(s) : parseInt(s, 10);
    onChange(clamp(isNaN(n) ? 0 : n));
  };
  const btn: React.CSSProperties = {
    width: compact ? 32 : 38, flexShrink: 0, fontSize: compact ? 15 : 18, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: compact ? "6px 0" : undefined,
  };
  const pad = 12 + (prefix ? 12 : 0);
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
      <button type="button" className="sp-btn sp-btn-ghost" style={btn} onClick={() => bump(-step)} aria-label="Decrease">−</button>
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        {prefix && (
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, fontWeight: 800, color: HEART.goldDark, pointerEvents: "none" }}>
            {prefix}
          </span>
        )}
        <input
          className={`${compact ? "sp-mini" : "sp-input"} sp-num`}
          type="text"
          inputMode={decimals ? "decimal" : "numeric"}
          style={{ textAlign: "center", paddingLeft: prefix ? pad + 8 : undefined, paddingRight: suffix ? 40 : undefined }}
          value={value === 0 ? "" : String(value)}
          placeholder="0"
          onChange={(e) => handle(e.target.value)}
        />
        {suffix && (
          <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", fontSize: 11, fontWeight: 700, color: HEART.textFaint, pointerEvents: "none" }}>
            {suffix}
          </span>
        )}
      </div>
      <button type="button" className="sp-btn sp-btn-ghost" style={btn} onClick={() => bump(step)} aria-label="Increase">+</button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   SEARCHABLE PICKER
   Every dropdown on this screen is this: a trigger that opens a
   card with a search box on top. Anchored panel on desktop,
   bottom sheet on mobile. Handles single and multi select.
═══════════════════════════════════════════════════════════ */
interface PickOption { id: string; label: string; sub?: string; hex?: string }

const SearchPicker: React.FC<{
  title: string;
  placeholder?: string;
  options: PickOption[];
  value: string | string[];
  multi?: boolean;
  isMobile: boolean;
  onChange: (v: any) => void;
}> = ({ title, placeholder = "Select…", options, value, multi, isMobile, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const picked: string[] = multi
    ? Array.isArray(value) ? value.map(String) : []
    : value ? [String(value)] : [];

  const label = picked.length
    ? picked.map((id) => options.find((o) => o.id === id)?.label ?? id).join(", ")
    : "";

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q));
  }, [options, query]);

  const place = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const width = Math.max(r.width, 260);
    setRect({
      top: Math.min(r.bottom + 6, window.innerHeight - 340),
      left: Math.min(r.left, window.innerWidth - width - 12),
      width,
    });
  }, []);

  useEffect(() => {
    if (!open || isMobile) return;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, isMobile, place]);

  useEffect(() => {
    if (!open || isMobile) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, isMobile]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const choose = (id: string) => {
    if (multi) {
      onChange(picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id]);
    } else {
      onChange(picked[0] === id ? "" : id);
      setOpen(false);
      setQuery("");
    }
  };

  const Body = (
    <>
      <div className="sp-pop-head">
        <div className="sp-pop-title">
          <p>{title}</p>
          <button
            className="sp-btn sp-btn-ghost"
            style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
            onClick={() => setOpen(false)}
          >
            <X size={13} />
          </button>
        </div>
        <div className="sp-searchbar" style={{ padding: "7px 12px" }}>
          <Search size={13} color={HEART.textFaint} />
          <input
            autoFocus={!isMobile}
            placeholder={`Search ${title.toLowerCase()}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{ background: HEART.border, border: "none", borderRadius: 999, width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
            >
              <X size={10} />
            </button>
          )}
        </div>
      </div>

      <div className="sp-pop-list" style={{ maxHeight: isMobile ? "48vh" : 260 }}>
        {rows.map((o) => {
          const on = picked.includes(o.id);
          return (
            <button key={o.id} type="button" className={`sp-opt${on ? " on" : ""}`} onClick={() => choose(o.id)}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                {o.hex && <span className="sp-varswatch" style={{ background: o.hex }} />}
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                  {o.sub && <span style={{ display: "block", fontSize: 10.5, fontWeight: 500, color: HEART.textFaint }}>{o.sub}</span>}
                </span>
              </span>
              {on && <Check size={14} />}
            </button>
          );
        })}
        {rows.length === 0 && (
          <p className="sp-hint" style={{ textAlign: "center", padding: "18px 0" }}>
            Nothing matches “{query}”.
          </p>
        )}
      </div>

      {(multi || picked.length > 0) && (
        <div className="sp-pop-foot">
          <button className="sp-btn sp-btn-ghost" style={{ flex: 1, padding: "9px 0", fontSize: 12.5 }} onClick={() => onChange(multi ? [] : "")}>
            Clear
          </button>
          {multi && (
            <button className="sp-btn sp-btn-primary" style={{ flex: 1, padding: "9px 0", fontSize: 12.5 }} onClick={() => setOpen(false)}>
              Done
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`sp-trigger${open ? " open" : ""}`}
        onClick={() => { setOpen((v) => !v); setQuery(""); }}
      >
        <span className={`sp-trigger-val${label ? "" : " sp-trigger-ph"}`}>{label || placeholder}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {multi && picked.length > 0 && <span className="sp-badge">{picked.length}</span>}
          <ChevronDown size={14} color={HEART.textFaint} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s" }} />
        </span>
      </button>

      {open && (
        isMobile ? (
          <>
            <div className="sp-sheet-backdrop" onClick={() => setOpen(false)} />
            <div className="sp-sheet">
              <div className="sp-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
              {Body}
            </div>
          </>
        ) : rect ? (
          <div ref={panelRef} className="sp-pop" style={{ top: rect.top, left: rect.left, width: rect.width }}>
            {Body}
          </div>
        ) : null
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   COLOUR SPHERES
═══════════════════════════════════════════════════════════ */
const ColorSpheres: React.FC<{
  options: any[];
  selected: string[];
  onToggle: (id: string) => void;
}> = ({ options, selected, onToggle }) => {
  const [query, setQuery] = useState("");
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => String(o.name).toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <>
      <div className="sp-searchbar" style={{ padding: "7px 12px", marginBottom: 10 }}>
        <Search size={13} color={HEART.textFaint} />
        <input placeholder="Search colours" value={query} onChange={(e) => setQuery(e.target.value)} />
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{ background: HEART.border, border: "none", borderRadius: 999, width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
          >
            <X size={10} />
          </button>
        )}
      </div>

      <div className="sp-colorgrid">
        {rows.map((opt) => {
          const id = String(opt.id);
          const on = selected.includes(id);
          const hex = opt.hex as string | undefined;
          const light = hex && LIGHT_HEX.has(hex.toUpperCase());
          return (
            <button key={id} type="button" className={`sp-colorcell${on ? " on" : ""}`} onClick={() => onToggle(id)} title={opt.name}>
              <span style={{ position: "relative", width: 40, height: 40 }}>
                <span
                  className={`sp-sphere${on ? " on" : ""}`}
                  style={{
                    background: hex
                      ? sphere3D(hex)
                      : "conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg)",
                    boxShadow: light
                      ? "inset -5px -4px 10px rgba(0,0,0,.12),inset 4px 4px 8px rgba(255,255,255,.6),0 1px 0 rgba(0,0,0,.15),0 4px 12px rgba(0,0,0,.12)"
                      : "inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20)",
                  }}
                />
                {on && <span className="sp-spheretick"><Check size={9} color="#fff" strokeWidth={3} /></span>}
              </span>
              <span className="sp-colorname">{opt.name}</span>
            </button>
          );
        })}
        {rows.length === 0 && <p className="sp-hint">No colour matches “{query}”.</p>}
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   VARIANT CARD — one per selected colour: its own photo,
   quantity, price and SKU. Price sits on the base price until
   someone moves it.
═══════════════════════════════════════════════════════════ */
const VariantCard: React.FC<{
  variant: Variant;
  baseRate: number;
  rateType: RateType;
  onUpdate: (colorId: string, patch: Partial<Variant>) => void;
  onRemove: (colorId: string) => void;
}> = ({ variant, baseRate, rateType, onUpdate, onRemove }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const shot = variant.previewUrl || variant.imageUrl;
  const inherits = variant.rate === null || variant.rate === undefined;
  const shownRate = inherits ? baseRate : (variant.rate as number);

  return (
    <div className="sp-variant">
      <div className="sp-varshot" onClick={() => fileRef.current?.click()}>
        {shot ? (
          <img src={shot} alt={variant.name} />
        ) : (
          <>
            <Camera size={17} color={HEART.redDark} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".08em", color: HEART.textMuted }}>PHOTO</span>
          </>
        )}
        {variant.previewUrl && !variant.imageUrl && (
          <span className="sp-thumb-tag" style={{ background: "rgba(27,14,6,.7)" }}>Pending</span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpdate(variant.colorId, { file: f, previewUrl: URL.createObjectURL(f) });
            e.target.value = "";
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sp-varhead">
          {variant.hex && <span className="sp-varswatch" style={{ background: variant.hex }} />}
          <span style={{ minWidth: 0 }}>
            <span className="sp-varname" style={{ display: "block" }}>{variant.name}</span>
            <span className="sp-varsku">{variant.sku || "SKU pending"}</span>
          </span>
          <button
            className="sp-btn"
            style={{ marginLeft: "auto", background: "none", color: HEART.textFaint, padding: 4, display: "flex" }}
            onClick={() => onRemove(variant.colorId)}
            aria-label={`Remove ${variant.name}`}
          >
            <X size={14} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: rateType === "perItem" ? "1fr 1fr" : "1fr", gap: 10 }}>
          <div>
            <label className="sp-label" style={{ marginBottom: 5 }}>Quantity</label>
            <NumberStepper compact value={variant.stock} onChange={(n) => onUpdate(variant.colorId, { stock: n })} step={1} />
          </div>

          {rateType === "perItem" ? (
            <div>
              <label className="sp-label" style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                Price <span style={{ color: HEART.textFaint }}>/ item</span>
                {inherits ? (
                  <span style={{ fontSize: 9, fontWeight: 800, color: HEART.textFaint }}>· base</span>
                ) : (
                  <button className="sp-inherit" onClick={() => onUpdate(variant.colorId, { rate: null })} title="Follow the base price again">
                    <RotateCcw size={9} /> reset
                  </button>
                )}
              </label>
              <NumberStepper
                compact
                decimals
                step={0.5}
                suffix="€"
                value={shownRate}
                onChange={(n) => onUpdate(variant.colorId, { rate: n })}
              />
            </div>
          ) : (
            <p className="sp-hint" style={{ marginTop: 2 }}>
              Priced per product — this colour sells at the listing price of {fmtMoney(baseRate)}.
              Switch to <strong>Per item</strong> to price it on its own.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RICH TEXT EDITOR
═══════════════════════════════════════════════════════════ */
const BLOCKS = [
  { label: "Paragraph", tag: "P" },
  { label: "Heading 1", tag: "H1" },
  { label: "Heading 2", tag: "H2" },
  { label: "Heading 3", tag: "H3" },
  { label: "Quote", tag: "BLOCKQUOTE" },
];

const TEXT_COLORS = [
  "#1B0E06", "#7A6653", "#C8202E", "#D4900F",
  "#3F8A4C", "#2F7BC8", "#7A3FC8", "#C83F9E",
  "#E4572E", "#2EA37A", "#111111", "#FFFFFF",
];

const toEmbedUrl = (url: string) => {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

const RichTextEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  resetKey: string | null;
  placeholder?: string;
}> = ({ value, onChange, resetKey, placeholder }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const blockMenuRef = useRef<HTMLDivElement>(null);
  const colorPopRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState(false);
  const [activeBlock, setActiveBlock] = useState("Paragraph");
  const [blockMenuOpen, setBlockMenuOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [active, setActive] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (blockMenuOpen && blockMenuRef.current && !blockMenuRef.current.contains(e.target as Node)) setBlockMenuOpen(false);
      if (colorOpen && colorPopRef.current && !colorPopRef.current.contains(e.target as Node)) setColorOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [blockMenuOpen, colorOpen]);

  /* Load stored HTML only when switching products, never per keystroke. */
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const syncFromDom = useCallback(() => {
    if (bodyRef.current) onChange(bodyRef.current.innerHTML);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
      const block = String(document.queryCommandValue("formatBlock")).toLowerCase();
      const match = BLOCKS.find((b) => b.tag.toLowerCase() === block);
      setActiveBlock(match ? match.label : "Paragraph");
    } catch {
      /* queryCommandState throws outside a focused editable in some browsers */
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const sel = window.getSelection();
      if (sel && bodyRef.current && bodyRef.current.contains(sel.anchorNode)) refreshActive();
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, [refreshActive]);

  const exec = (cmd: string, arg?: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, arg);
    syncFromDom();
    refreshActive();
  };

  const captureSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  };

  /** Insert at the saved caret directly — execCommand silently no-ops once
      user activation has expired (e.g. after an async FileReader callback). */
  const insertNodeAtSavedRange = (node: Node) => {
    if (!bodyRef.current) return;
    bodyRef.current.focus();
    const sel = window.getSelection();
    const range = savedRangeRef.current;
    if (sel && range) {
      sel.removeAllRanges();
      sel.addRange(range);
      range.deleteContents();
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      bodyRef.current.appendChild(node);
    }
    syncFromDom();
  };

  const submitLink = () => {
    if (!linkUrl.trim()) return setLinkModalOpen(false);
    const a = document.createElement("a");
    a.href = linkUrl.trim();
    a.target = "_blank";
    a.rel = "noopener";
    a.textContent = linkText.trim() || linkUrl.trim();
    insertNodeAtSavedRange(a);
    setLinkModalOpen(false);
  };

  const handleImageFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = document.createElement("img");
      img.src = String(reader.result);
      insertNodeAtSavedRange(img);
    };
    reader.readAsDataURL(file);
  };

  const submitVideo = () => {
    if (!videoUrl.trim()) return setVideoModalOpen(false);
    const iframe = document.createElement("iframe");
    iframe.src = toEmbedUrl(videoUrl.trim());
    iframe.setAttribute("frameborder", "0");
    iframe.allowFullscreen = true;
    iframe.style.width = "100%";
    iframe.style.aspectRatio = "16/9";
    insertNodeAtSavedRange(iframe);
    setVideoModalOpen(false);
  };

  const noBlur = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className={`sp-editor${focused ? " focused" : ""}`}>
      <div className="sp-editor-toolbar">
        <div ref={blockMenuRef} style={{ position: "relative" }}>
          <button type="button" className="sp-etool-select" onMouseDown={noBlur} onClick={() => setBlockMenuOpen((v) => !v)}>
            {activeBlock} <ChevronDown size={13} />
          </button>
          {blockMenuOpen && (
            <div className="sp-fade" style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff",
              border: `1px solid ${HEART.border}`, borderRadius: 12, boxShadow: "0 10px 24px rgba(27,14,6,.15)",
              width: 160, zIndex: 30, padding: 5,
            }}>
              {BLOCKS.map((b) => (
                <button
                  key={b.tag}
                  type="button"
                  onMouseDown={noBlur}
                  onClick={() => { exec("formatBlock", `<${b.tag}>`); setBlockMenuOpen(false); }}
                  style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: 9, padding: "9px 10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", color: HEART.dark }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="sp-etool-sep" />
        <button type="button" className={`sp-etool${active.bold ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("bold")} title="Bold"><Bold size={14} /></button>
        <button type="button" className={`sp-etool${active.italic ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("italic")} title="Italic"><Italic size={14} /></button>
        <button type="button" className={`sp-etool${active.underline ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("underline")} title="Underline"><Underline size={14} /></button>

        <div ref={colorPopRef} style={{ position: "relative" }}>
          <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => setColorOpen((v) => !v)} title="Text colour">
            <Palette size={14} />
          </button>
          {colorOpen && (
            <div className="sp-color-pop sp-fade" style={{ width: 190 }}>
              <p className="sp-color-pop-label">Text colour</p>
              <div className="sp-color-grid">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="sp-color-swatch"
                    style={{ background: c }}
                    title={c}
                    onMouseDown={noBlur}
                    onClick={() => { exec("foreColor", c); setColorOpen(false); }}
                  />
                ))}
              </div>
              <p className="sp-color-pop-label" style={{ marginTop: 12 }}>Highlight</p>
              <div className="sp-color-grid">
                {TEXT_COLORS.slice(0, 6).map((c) => (
                  <button
                    key={`bg-${c}`}
                    type="button"
                    className="sp-color-swatch"
                    style={{ background: c }}
                    title={c}
                    onMouseDown={noBlur}
                    onClick={() => { exec("hiliteColor", c); setColorOpen(false); }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <span className="sp-etool-sep" />
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("insertUnorderedList")} title="Bullet list"><ListIcon size={14} /></button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered size={14} /></button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("formatBlock", "<BLOCKQUOTE>")} title="Quote"><Quote size={14} /></button>
        <span className="sp-etool-sep" />
        <button
          type="button" className="sp-etool" onMouseDown={noBlur} title="Insert link"
          onClick={() => {
            captureSelection();
            const sel = window.getSelection();
            setLinkText(sel ? sel.toString() : "");
            setLinkUrl("");
            setLinkModalOpen(true);
          }}
        >
          <Link2 size={14} />
        </button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => { captureSelection(); fileInputRef.current?.click(); }} title="Insert image"><ImageIcon size={14} /></button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => { captureSelection(); setVideoUrl(""); setVideoModalOpen(true); }} title="Insert video"><Video size={14} /></button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("justifyLeft")} title="Align left"><AlignLeft size={14} /></button>
        <span style={{ flex: 1 }} />
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("undo")} title="Undo"><Undo2 size={14} /></button>
        <button type="button" className="sp-etool" onMouseDown={noBlur} onClick={() => exec("redo")} title="Redo"><Redo2 size={14} /></button>
      </div>

      <div
        ref={bodyRef}
        className="sp-editor-body"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={syncFromDom}
        onFocus={() => { setFocused(true); refreshActive(); }}
        onBlur={() => setFocused(false)}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => { handleImageFile(e.target.files?.[0] || null); e.target.value = ""; }}
      />

      {linkModalOpen && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setLinkModalOpen(false)} />
          <div className="sp-modal">
            <h3>Insert link</h3>
            <label className="sp-label">Link text</label>
            <input className="sp-input" placeholder="e.g. our size guide" value={linkText} onChange={(e) => setLinkText(e.target.value)} />
            <label className="sp-label" style={{ marginTop: 14 }}>URL</label>
            <input
              className="sp-input"
              placeholder="https://…"
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitLink()}
            />
            <p className="sp-hint">Leave the text blank to show the URL itself.</p>
            <div className="sp-modal-actions">
              <button className="sp-btn sp-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setLinkModalOpen(false)}>Cancel</button>
              <button className="sp-btn sp-btn-primary" style={{ flex: 1, padding: "11px 0" }} onClick={submitLink}>Insert</button>
            </div>
          </div>
        </>
      )}

      {videoModalOpen && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setVideoModalOpen(false)} />
          <div className="sp-modal">
            <h3>Insert video</h3>
            <label className="sp-label">Video URL</label>
            <input
              className="sp-input"
              placeholder="YouTube, Vimeo, or a direct link"
              autoFocus
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitVideo()}
            />
            <p className="sp-hint">Paste a YouTube or Vimeo link and it embeds automatically.</p>
            <div className="sp-modal-actions">
              <button className="sp-btn sp-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setVideoModalOpen(false)}>Cancel</button>
              <button className="sp-btn sp-btn-primary" style={{ flex: 1, padding: "11px 0" }} onClick={submitVideo}>Insert</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   COUNTRY PICKER — searchable 3D sphere of flags
═══════════════════════════════════════════════════════════ */
const CountryPicker: React.FC<{
  value: string;
  onChange: (code: string) => void;
  isMobile: boolean;
}> = ({ value, onChange, isMobile }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const groupRef = useRef<HTMLDivElement>(null);
  const angleRef = useRef(0);
  const draggingRef = useRef(false);
  const lastXRef = useRef(0);
  const rafRef = useRef<number | undefined>(undefined);
  const selected = Countries.find((c) => c.code === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return Countries;
    return Countries.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [query]);

  /* Fibonacci sphere: evenly distribute badges around a sphere. */
  const points = useMemo(() => {
    const R = 128;
    const N = Math.max(filtered.length, 1);
    return filtered.map((c, i) => {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / N);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      return {
        ...c,
        x: R * Math.sin(phi) * Math.cos(theta),
        y: R * Math.sin(phi) * Math.sin(theta),
        z: R * Math.cos(phi),
      };
    });
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const tick = () => {
      if (!draggingRef.current) angleRef.current += 0.15;
      groupRef.current?.style.setProperty("--angle", String(angleRef.current));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [open]);

  const Body = (
    <>
      <div className="sp-sheet-header">
        <p style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Country of origin</p>
        <button
          className="sp-btn sp-btn-ghost"
          style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
          onClick={() => setOpen(false)}
        >
          <X size={15} />
        </button>
      </div>

      <div style={{ padding: "0 18px" }}>
        <div className="sp-searchbar">
          <Search size={14} color={HEART.textFaint} />
          <input autoFocus placeholder="Search countries" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{ background: HEART.border, border: "none", borderRadius: 999, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      <div
        className="sp-sphere-wrap"
        onPointerDown={(e) => { draggingRef.current = true; lastXRef.current = e.clientX; (e.target as HTMLElement).setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          angleRef.current += (e.clientX - lastXRef.current) * 0.5;
          lastXRef.current = e.clientX;
          groupRef.current?.style.setProperty("--angle", String(angleRef.current));
        }}
        onPointerUp={() => { draggingRef.current = false; }}
        onPointerLeave={() => { draggingRef.current = false; }}
      >
        <div className="sp-sphere-group" ref={groupRef}>
          {points.map((pt) => (
            <div key={pt.code} className="sp-sphere-pt" style={{ transform: `translate3d(${pt.x}px, ${pt.y}px, ${pt.z}px)` }}>
              <button
                type="button"
                className={`sp-sphere-badge${pt.code === value ? " selected" : ""}`}
                onClick={() => { onChange(pt.code); setOpen(false); setQuery(""); }}
              >
                <span>{pt.emoji}</span>
                <span>{pt.code}</span>
              </button>
            </div>
          ))}
        </div>
      </div>
      <p className="sp-hint" style={{ padding: "0 18px 16px", textAlign: "center" }}>Drag to spin · tap a flag to select</p>
    </>
  );

  return (
    <>
      <button type="button" className="sp-trigger" onClick={() => setOpen(true)}>
        {selected ? (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>{selected.emoji}</span>
            <span>{selected.name}</span>
          </span>
        ) : (
          <span className="sp-trigger-ph">Select a country</span>
        )}
        <ChevronDown size={14} color={HEART.textFaint} />
      </button>

      {open && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setOpen(false)} />
          {isMobile ? (
            <div className="sp-sheet">
              <div className="sp-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
              {Body}
            </div>
          ) : (
            <div className="sp-bigmodal">{Body}</div>
          )}
        </>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   CATEGORY PICKER — department → category → subcategory →
   product type → product. Any level can be chosen.
═══════════════════════════════════════════════════════════ */
const CategoryPicker: React.FC<{
  value: string;
  onChange: (id: string, label: string) => void;
  isMobile: boolean;
}> = ({ value, onChange, isMobile }) => {
  const [open, setOpen] = useState(false);
  const [parent, setParent] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const selected = value ? NODES.get(value) : undefined;

  const level = useMemo(
    () => (parent === null ? DEPARTMENTS : AllCategoriesFlat.filter((c) => c.parentId === parent)),
    [parent]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return level;
    return AllCategoriesFlat.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 60);
  }, [level, query]);

  const crumbs = parent ? catPath(parent) : [];

  const pick = (item: CategoryItem) => {
    onChange(item.id, item.name);
    setOpen(false);
    setQuery("");
    setParent(null);
  };

  const stepLabel: Record<string, string> = {
    department: "category",
    category: "sub-category",
    subCategory: "product type",
    productType: "product",
  };

  const Body = (
    <>
      <div className="sp-sheet-header">
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          {parent !== null && !query && (
            <button
              className="sp-btn sp-btn-ghost"
              style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
              onClick={() => {
                const chain = catPath(parent);
                setParent(chain.length >= 2 ? chain[chain.length - 2].id : null);
              }}
              title="Back"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <p style={{ fontSize: 15, fontWeight: 800, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {query
              ? "Search results"
              : parent
              ? `${NODES.get(parent)?.name} — pick a ${stepLabel[NODES.get(parent)?.level ?? ""] ?? "type"}`
              : "Pick a department"}
          </p>
        </div>
        <button
          className="sp-btn sp-btn-ghost"
          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
          onClick={() => setOpen(false)}
        >
          <X size={15} />
        </button>
      </div>

      <div style={{ padding: "0 18px 10px" }}>
        <div className="sp-searchbar">
          <Search size={14} color={HEART.textFaint} />
          <input autoFocus placeholder="Search all categories" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{ background: HEART.border, border: "none", borderRadius: 999, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {!query && (
        <div className="sp-cat-crumbs">
          <span className="sp-cat-crumb" onClick={() => setParent(null)}>All</span>
          {crumbs.map((c) => (
            <React.Fragment key={c.id}>
              <span className="sp-cat-crumb-sep">›</span>
              <span className="sp-cat-crumb" onClick={() => setParent(c.id)}>{c.name}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="sp-cat-list" style={{ maxHeight: isMobile ? "50vh" : 360 }}>
        {rows.map((item) => {
          const drillable = hasChildren(item.id) && !query;
          return (
            <div key={item.id} className={`sp-cat-row${value === item.id ? " sel" : ""}`}>
              <div
                style={{ display: "flex", alignItems: "center", gap: 11, flex: 1, minWidth: 0, cursor: "pointer" }}
                onClick={() => (drillable ? setParent(item.id) : pick(item))}
              >
                <div className="sp-cat-ic">{item.icon}</div>
                <span className="sp-cat-name">
                  <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</span>
                  {query && (
                    <span style={{ display: "block", fontSize: 10.5, fontWeight: 500, color: HEART.textFaint }}>
                      {catPath(item.id).slice(0, -1).map((p) => p.name).join(" › ")}
                    </span>
                  )}
                </span>
              </div>
              <button className="sp-cat-pick" onClick={(e) => { e.stopPropagation(); pick(item); }}>Choose</button>
              {drillable && (
                <ChevronDown
                  size={16}
                  style={{ transform: "rotate(-90deg)", color: HEART.textFaint, cursor: "pointer", flexShrink: 0 }}
                  onClick={() => setParent(item.id)}
                />
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="sp-hint" style={{ textAlign: "center", padding: "20px 0" }}>Nothing matches “{query}”.</p>
        )}
      </div>
    </>
  );

  return (
    <>
      <button type="button" className="sp-trigger" onClick={() => setOpen(true)}>
        {selected ? (
          <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <span style={{ color: HEART.goldDark, display: "flex" }}>{selected.icon}</span>
            <span className="sp-trigger-val">{pathLabel(selected.id)}</span>
          </span>
        ) : (
          <span className="sp-trigger-ph">Select a category</span>
        )}
        <ChevronDown size={14} color={HEART.textFaint} />
      </button>

      {open && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setOpen(false)} />
          {isMobile ? (
            <div className="sp-sheet">
              <div className="sp-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
              {Body}
            </div>
          ) : (
            <div className="sp-bigmodal">{Body}</div>
          )}
        </>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE FIELDS — every option, unit and input type comes
   from the attribute collection. Nothing is written here.
═══════════════════════════════════════════════════════════ */
const AttributeFields: React.FC<{
  attrs: any[];
  values: Record<string, any>;
  isMobile: boolean;
  colorAttrId: string | null;
  onChange: (attrId: string, value: any) => void;
  onToggleColor: (colorId: string) => void;
}> = ({ attrs, values, isMobile, colorAttrId, onChange, onToggleColor }) => (
  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
    {attrs.map((attr: any) => {
      const key = String(attr.id);
      const opts: PickOption[] = (attr.options ?? []).map((o: any) => ({
        id: String(o.id),
        label: String(o.name || o.standard || o.id),
        sub: o.eu || o.uk || o.us ? `EU ${o.eu ?? "—"} · UK ${o.uk ?? "—"} · US ${o.us ?? "—"}` : undefined,
        hex: o.hex,
      }));
      const label = (
        <label className="sp-label">
          {attr.name}
          {attr.required && <span className="sp-attr-req">*</span>}
        </label>
      );

      if (key === colorAttrId) {
        const picked: string[] = Array.isArray(values[key]) ? values[key].map(String) : [];
        return (
          <div key={key} className="sp-attr-field wide">
            {label}
            <ColorSpheres options={attr.options ?? []} selected={picked} onToggle={onToggleColor} />
            <p className="sp-hint">Each colour gets its own photo, quantity, price and SKU below.</p>
          </div>
        );
      }

      return (
        <div key={key} className="sp-attr-field">
          {label}

          {(attr.inputType === "select" || !attr.inputType) && (
            <SearchPicker
              title={attr.name}
              placeholder={`Choose ${String(attr.name).toLowerCase()}`}
              options={opts}
              value={values[key] ?? ""}
              isMobile={isMobile}
              onChange={(v) => onChange(key, v)}
            />
          )}

          {attr.inputType === "multiselect" && (
            <SearchPicker
              multi
              title={attr.name}
              placeholder={`Choose ${String(attr.name).toLowerCase()}`}
              options={opts}
              value={Array.isArray(values[key]) ? values[key] : []}
              isMobile={isMobile}
              onChange={(v) => onChange(key, v)}
            />
          )}

          {attr.inputType === "text" && (
            <input
              className="sp-input"
              value={values[key] ?? ""}
              onChange={(e) => onChange(key, e.target.value)}
              placeholder={`Enter ${String(attr.name).toLowerCase()}`}
            />
          )}

          {attr.inputType === "number" && (
            <NumberStepper
              value={Number(values[key]) || 0}
              onChange={(n) => onChange(key, n)}
              step={1}
              decimals
              suffix={attr.unit}
            />
          )}

          {attr.required && <p className="sp-hint">Needed before this can go live.</p>}
        </div>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   PRICING & INVENTORY
═══════════════════════════════════════════════════════════ */
const PricingInventoryFields: React.FC<{
  form: FormShape;
  hasVariants: boolean;
  variantCount: number;
  onGenerateSku: () => void;
  set: <K extends keyof FormShape>(key: K, value: FormShape[K]) => void;
}> = ({ form, hasVariants, variantCount, onGenerateSku, set }) => (
  <>
    <p className="sp-cardtitle" style={{ marginBottom: 12 }}>How you charge</p>

    <div className="sp-rategrid" style={{ marginBottom: 14 }}>
      {(["perProduct", "perItem"] as RateType[]).map((rt) => (
        <button
          key={rt}
          type="button"
          className={`sp-ratecard${form.rateType === rt ? " on" : ""}`}
          onClick={() => set("rateType", rt)}
        >
          <span className="sp-ratecard-ic">
            {rt === "perProduct" ? <Package size={15} /> : <Layers size={15} />}
          </span>
          <span style={{ minWidth: 0 }}>
            <p className="sp-ratecard-t">{RATE_COPY[rt].title}</p>
            <p className="sp-ratecard-b">{RATE_COPY[rt].blurb}</p>
          </span>
        </button>
      ))}
    </div>

    <div className="sp-bigrate">
      <span className="sp-bigrate-n">{fmtMoney(form.rate)}</span>
      <span className="sp-bigrate-u">{RATE_LABEL[form.rateType]}</span>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
      <div>
        <label className="sp-label">Base price {RATE_LABEL[form.rateType]}</label>
        <NumberStepper value={form.rate} onChange={(n) => set("rate", n)} step={1} decimals prefix="€" />
        {hasVariants && (
          <p className="sp-hint">
            {form.rateType === "perItem"
              ? "Every colour starts here until you give it its own price."
              : "One price covers the listing — colours share it."}
          </p>
        )}
      </div>
      <div>
        <label className="sp-label">Compare-at price</label>
        <NumberStepper value={form.compareAtPrice} onChange={(n) => set("compareAtPrice", n)} step={1} decimals prefix="€" />
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "end" }}>
      <div>
        <label className="sp-label">Cost {RATE_LABEL[form.rateType]}</label>
        <NumberStepper value={form.costPerItem} onChange={(n) => set("costPerItem", n)} step={1} decimals prefix="€" />
        {form.rate > 0 && form.costPerItem > 0 && (
          <p className="sp-hint">
            Margin <span className="sp-num">{Math.round(((form.rate - form.costPerItem) / form.rate) * 100)}</span>%
            · <span className="sp-num">{fmtMoney(form.rate - form.costPerItem)}</span> kept.
          </p>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 11px" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700 }}>Charge tax</span>
        <button className={`sp-toggle${form.chargeTax ? " on" : ""}`} onClick={() => set("chargeTax", !form.chargeTax)} aria-label="Charge tax">
          <span />
        </button>
      </div>
    </div>

    {form.compareAtPrice > form.rate && form.rate > 0 && (
      <p className="sp-hint">
        Shows as <span style={{ textDecoration: "line-through" }}>{fmtMoney(form.compareAtPrice)}</span> struck through —
        a <span className="sp-num">{Math.round((1 - form.rate / form.compareAtPrice) * 100)}</span>% saving.
      </p>
    )}

    <hr className="sp-section-divider" />

    <p className="sp-cardtitle" style={{ marginBottom: 12 }}>Inventory</p>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
      <div>
        <label className="sp-label">Quantity</label>
        {hasVariants ? (
          <>
            <div
              className="sp-input"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: HEART.panel2, cursor: "not-allowed" }}
            >
              <span className="sp-num" style={{ fontSize: 16 }}>{form.stock}</span>
              <span style={{ fontSize: 11.5, color: HEART.textFaint, fontWeight: 600 }}>
                across {variantCount} {variantCount === 1 ? "colour" : "colours"}
              </span>
            </div>
            <p className="sp-hint">Added up from the colours above — edit it there.</p>
          </>
        ) : (
          <NumberStepper value={form.stock} onChange={(n) => set("stock", n)} step={1} />
        )}
      </div>
      <div>
        <label className="sp-label">SKU</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="sp-input"
            style={{ fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", letterSpacing: ".02em" }}
            placeholder="Auto on save"
            value={form.sku}
            onChange={(e) => set("sku", e.target.value.toUpperCase())}
          />
          <button
            className="sp-btn sp-btn-ghost"
            style={{ padding: "0 13px", flexShrink: 0, display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}
            onClick={onGenerateSku}
            title="Generate a fresh SKU"
          >
            <Wand2 size={14} />
          </button>
        </div>
        <p className="sp-hint">Left blank, one is generated on save — unique across your store.</p>
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "end" }}>
      <div>
        <label className="sp-label">Barcode</label>
        <input className="sp-input" value={form.barcode} onChange={(e) => set("barcode", e.target.value)} placeholder="ISBN, UPC, GTIN…" />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 11px" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700 }}>Sell when out of stock</span>
        <button className={`sp-toggle${form.sellWhenOutOfStock ? " on" : ""}`} onClick={() => set("sellWhenOutOfStock", !form.sellWhenOutOfStock)} aria-label="Sell when out of stock">
          <span />
        </button>
      </div>
    </div>
  </>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const StoreProducts: React.FC<{ brandId: string; listingId?: string }> = ({ brandId, listingId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();

  const { brandProducts, brandProductsLoading } = useSelector((state: RootState) => state.storeProducts);

  /* The brand this screen is listing for. Adjust the lookup if your
     commerce-partner slice keys brands by something other than brandId. */
  const brandName = useSelector((state: RootState) => {
    const s = state as any;
    const partners: any[] = s.commercePartners?.partners ?? [];
    const match = partners.find(
      (p) => p?.brandId === brandId || p?.id === brandId || p?.listingId === brandId
    );
    return (
      match?.brandName ??
      match?.name ??
      s.storeProducts?.brand?.brandName ??
      ""
    );
  });

  const view: ViewMode = listingId ? "form" : "list";

  const [editingId, setEditingId] = useState<string | null>(
    listingId && listingId !== "new" ? listingId : null
  );
  const [form, setForm] = useState<FormShape>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [removingPhoto, setRemovingPhoto] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "draft">("all");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const [pricingSheetOpen, setPricingSheetOpen] = useState(false);
  const [attrSheetOpen, setAttrSheetOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setDescription = useCallback((html: string) => set("description", html), []);

  /* ── attributes for the chosen category ── */
  const { key: attrKey, attrs: rawAttrs } = useMemo(
    () => resolveAttributes(form.categoryId),
    [form.categoryId]
  );

  const attrs = useMemo(() => {
    if (!form.categoryId) return [];
    const dept = deptOf(form.categoryId);
    const colourIsReal = declaresColor(attrKey) || !COLOURLESS_DEPTS.has(dept);
    return rawAttrs.filter((a) => (isColorAttr(a) ? colourIsReal : true));
  }, [rawAttrs, attrKey, form.categoryId]);

  const colorAttr = useMemo(() => attrs.find(isColorAttr), [attrs]);
  const colorAttrId = colorAttr ? String(colorAttr.id) : null;
  const colorOptions = (colorAttr?.options ?? []) as any[];

  const selectedColors: string[] = useMemo(() => {
    if (!colorAttrId) return [];
    const v = form.attributes[colorAttrId];
    return Array.isArray(v) ? v.map(String) : [];
  }, [form.attributes, colorAttrId]);

  const variantList = useMemo(
    () => selectedColors.map((id) => form.variants[id]).filter(Boolean) as Variant[],
    [selectedColors, form.variants]
  );
  const hasVariants = variantList.length > 0;

  /* Stock is the sum of what's sitting under each colour. A seller who has
     added colours but not typed quantities yet clearly has at least one of
     each, so fall back to the colour count rather than showing zero. */
  const resolvedStock = useMemo(() => {
    if (!hasVariants) return form.stock;
    const total = variantList.reduce((s, v) => s + (Number(v.stock) || 0), 0);
    return total > 0 ? total : variantList.length;
  }, [hasVariants, variantList, form.stock]);

  /* Keep the field in step with the colours so what's on screen is what saves. */
  useEffect(() => {
    if (!hasVariants) return;
    if (form.stock === resolvedStock) return;
    setForm((f) => ({ ...f, stock: resolvedStock }));
  }, [hasVariants, resolvedStock, form.stock]);

  /* Vendor defaults to the brand doing the selling — most listings never
     need anything else, and an empty field just makes work. */
  useEffect(() => {
    if (!brandName || form.vendor.trim()) return;
    setForm((f) => (f.vendor.trim() ? f : { ...f, vendor: brandName }));
  }, [brandName, form.vendor]);

  /* ── SKUs already spoken for, anywhere in the brand ── */
  const takenSkus = useMemo(() => {
    const s = new Set<string>();
    brandProducts.forEach((p: any) => {
      if (p.listingId === editingId) return;
      if (p.sku) s.add(String(p.sku).toUpperCase());
      Object.values(p.variants || {}).forEach((v: any) => v?.sku && s.add(String(v.sku).toUpperCase()));
    });
    return s;
  }, [brandProducts, editingId]);

  const generateSku = useCallback(() => {
    const categoryName = NODES.get(form.categoryId)?.name ?? form.category;
    return makeSku(form.name || "PRODUCT", categoryName || "GEN", takenSkus);
  }, [form.name, form.category, form.categoryId, takenSkus]);

  const handleGenerateSku = () => {
    const sku = generateSku();
    setForm((f) => {
      const variants = { ...f.variants };
      const used = new Set(takenSkus);
      Object.keys(variants).forEach((cid) => {
        const next = makeVariantSku(sku, variants[cid].name, used);
        used.add(next);
        variants[cid] = { ...variants[cid], sku: next };
      });
      return { ...f, sku, variants };
    });
    setToast("New SKU generated");
  };

  /* ── colour ⇄ variant sync ── */
  const toggleColor = (colorId: string) => {
    if (!colorAttrId) return;
    setForm((f) => {
      const current: string[] = Array.isArray(f.attributes[colorAttrId])
        ? f.attributes[colorAttrId].map(String)
        : [];
      const on = current.includes(colorId);
      const nextIds = on ? current.filter((c) => c !== colorId) : [...current, colorId];
      const variants = { ...f.variants };

      if (on) {
        const gone = variants[colorId];
        if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
        delete variants[colorId];
      } else {
        const opt = colorOptions.find((o) => String(o.id) === colorId);
        const used = new Set([...takenSkus, ...Object.values(variants).map((v) => v.sku).filter(Boolean)]);
        variants[colorId] = {
          colorId,
          name: opt?.name ?? "Colour",
          hex: opt?.hex,
          imageUrl: "",
          stock: 0,
          rate: null, // follows the base price until changed
          sku: makeVariantSku(f.sku, opt?.name ?? "COL", used),
        };
      }
      return { ...f, attributes: { ...f.attributes, [colorAttrId]: nextIds }, variants };
    });
  };

  const updateVariant = (colorId: string, patch: Partial<Variant>) =>
    setForm((f) => {
      const existing = f.variants[colorId];
      if (!existing) return f;
      return { ...f, variants: { ...f.variants, [colorId]: { ...existing, ...patch } } };
    });

  /* ── hydrate ── */
  const hydrate = (p: any): FormShape => {
    const variants: Record<string, Variant> = {};
    Object.entries(p.variants || {}).forEach(([k, v]: [string, any]) => {
      variants[String(k)] = {
        colorId: String(k),
        name: v?.name ?? "Colour",
        hex: v?.hex ?? undefined,
        imageUrl: v?.imageUrl ?? "",
        stock: Number(v?.stock) || 0,
        rate: v?.rate === null || v?.rate === undefined ? null : Number(v.rate),
        sku: v?.sku ?? "",
      };
    });
    return {
      name: p.name || "",
      description: p.description || "",
      category: p.category || "",
      categoryId: p.categoryId || "",
      vendor: p.vendor || "",
      tags: p.tags || [],
      rate: p.rate || 0,
      rateType: (p.rateType as RateType) || DEFAULT_RATE_TYPE,
      compareAtPrice: p.compareAtPrice || 0,
      costPerItem: p.costPerItem || 0,
      chargeTax: p.chargeTax ?? false,
      stock: p.stock || 0,
      sku: p.sku || "",
      barcode: p.barcode || "",
      sellWhenOutOfStock: p.sellWhenOutOfStock || false,
      weight: p.weight || 0,
      originCountry: p.originCountry || "",
      isPublished: p.isPublished,
      isDraft: p.isDraft ?? !p.isPublished,
      photos: p.photos || [],
      attributes: p.attributes || {},
      variants,
    };
  };

  useEffect(() => {
    if (!listingId || listingId === "new") {
      setEditingId(null);
      setForm(emptyForm());
      return;
    }
    if (listingId === editingId) return; // already filled by openEditProduct
    const existing = brandProducts.find((p) => p.listingId === listingId);
    if (existing) {
      setEditingId(existing.listingId);
      setForm(hydrate(existing));
    }
    // still loading? this re-runs when the listener delivers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, brandProducts]);

  /* ── brand-scoped listener ── */
  useEffect(() => {
    if (!brandId) return;
    dispatch(listenToBrandProductsFeed(brandId) as any);
    return () => {
      dispatch(stopBrandProductsFeed() as any);
    };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  /* ── filtering ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return brandProducts.filter((p: any) => {
      const matchesQuery =
        !q ||
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && p.isPublished) ||
        (statusFilter === "draft" && !p.isPublished);
      return matchesQuery && matchesStatus;
    });
  }, [brandProducts, search, statusFilter]);

  const counts = useMemo(
    () => ({
      all: brandProducts.length,
      active: brandProducts.filter((p) => p.isPublished).length,
      draft: brandProducts.filter((p) => !p.isPublished).length,
    }),
    [brandProducts]
  );

  /* ── selection & bulk ── */
  const toggleSelect = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () =>
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((p) => p.listingId))
    );

  const bulkSetPublished = async (published: boolean) => {
    await Promise.all(
      Array.from(selectedIds).map((id) =>
        dispatch(upsertProduct({ listingId: id, data: { isPublished: published, isDraft: !published } as any }))
          .unwrap().catch(() => {})
      )
    );
    setToast(published ? "Products set to active" : "Products set to draft");
    setSelectedIds(new Set());
  };

  const bulkDelete = async () => {
    await Promise.all(Array.from(selectedIds).map((id) => dispatch(deleteProduct(id))));
    setToast("Products deleted");
    setSelectedIds(new Set());
  };

  /* ── navigation ── */
  const openNewProduct = () => navigate(`/store/${brandId}/products/new`);
  const backToList = () => navigate(`/store/${brandId}/products`);

  const openEditProduct = (p: Product) => {
    setEditingId(p.listingId);
    setForm(hydrate(p));
    navigate(`/store/${brandId}/products/${p.listingId}`);
  };

  /* ── media staging ── */
  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    const picked = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!picked.length) return;
    setPendingPhotos((prev) => [...prev, ...picked]);
    setPendingPreviews((prev) => [...prev, ...picked.map((f) => URL.createObjectURL(f))]);
  };

  const handleRemoveSavedPhoto = async (url: string) => {
    if (!editingId) {
      setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== url) }));
      return;
    }
    setRemovingPhoto(url);
    try {
      await dispatch(deleteProductMedia({ listingId: editingId, photoUrl: url })).unwrap();
      setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== url) }));
      setToast("Photo removed");
    } catch (err: any) {
      setToast(err?.message || "Could not remove photo");
    } finally {
      setRemovingPhoto(null);
    }
  };

  const handleRemovePendingPhoto = (index: number) => {
    URL.revokeObjectURL(pendingPreviews[index]);
    setPendingPhotos((prev) => prev.filter((_, i) => i !== index));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── save ──
     Three passes, because variant photos need a listingId that
     doesn't exist until the doc is written:
       1. write the product (variants carry whatever URLs they have)
       2. upload any staged variant photos
       3. rewrite the variants map with the fresh URLs             */
  const handleSave = async () => {
    if (!form.name.trim()) {
      setToast("Give the product a name first");
      return;
    }
    setSaving(true);
    try {
      const sku = form.sku.trim() || generateSku();
      const pricedPerItem = form.rateType === "perItem" && variantList.length > 0;

      /* Fill any variant that never got a SKU (e.g. base SKU arrived later). */
      const usedSkus = new Set(takenSkus);
      const variantsForSave: Record<string, any> = {};
      variantList.forEach((v) => {
        const vSku = v.sku || makeVariantSku(sku, v.name, usedSkus);
        usedSkus.add(vSku);
        variantsForSave[v.colorId] = {
          colorId: v.colorId,
          name: v.name,
          hex: v.hex ?? null,
          imageUrl: v.imageUrl || "",
          stock: v.stock || 0,
          /* Priced per item: every colour stores a real number, resolving
             "follow the base price" now so nothing reads back as null.
             Priced per product: the price lives on the doc, not the colour. */
          rate: pricedPerItem ? (v.rate ?? form.rate ?? 0) : null,
          sku: vSku,
        };
      });

      const payload: any = {
        ...form,
        sku,
        variants: variantsForSave,
        /* Nothing to keep on the doc when each colour carries its own price. */
        rate: pricedPerItem ? null : form.rate,
        stock: resolvedStock,
        isPublished: form.isPublished,
        isDraft: !form.isPublished,
      };
      /* photos are written through upsertProductMedia instead */
      delete payload.photos;

      const saved = await dispatch(
        upsertProduct({ listingId: editingId || undefined, brandId, data: payload })
      ).unwrap();

      /* staged gallery photos */
      if (pendingPhotos.length) {
        setUploading(true);
        await dispatch(
          upsertProductMedia({ listingId: saved.listingId, brandId, files: pendingPhotos, type: "photo" })
        ).unwrap();
        pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
        setPendingPhotos([]);
        setPendingPreviews([]);
      }

      /* staged variant photos — collect first, write state once */
      const withFiles = variantList.filter((v) => v.file);
      if (withFiles.length) {
        setUploading(true);
        const uploaded: Record<string, string> = {};
        for (const v of withFiles) {
          const res = await dispatch(
            upsertProductMedia({ listingId: saved.listingId, brandId, files: [v.file as File], type: "photo" })
          ).unwrap();
          const url = res.downloadURLs?.[0];
          if (url) uploaded[v.colorId] = url;
        }

        if (Object.keys(uploaded).length) {
          Object.entries(uploaded).forEach(([cid, url]) => {
            variantsForSave[cid].imageUrl = url;
          });
          await dispatch(
            upsertProduct({ listingId: saved.listingId, brandId, data: { variants: variantsForSave } as any })
          ).unwrap();

          setForm((f) => {
            const variants = { ...f.variants };
            Object.entries(uploaded).forEach(([cid, url]) => {
              if (!variants[cid]) return;
              if (variants[cid].previewUrl) URL.revokeObjectURL(variants[cid].previewUrl as string);
              variants[cid] = { ...variants[cid], imageUrl: url, file: undefined, previewUrl: undefined };
            });
            return { ...f, sku, variants };
          });
        }
      }

      setToast(editingId ? "Product updated" : "Product created");
      navigate(`/store/${brandId}/products`);
    } catch (err: any) {
      setToast(err?.message || "Could not save product");
    } finally {
      setUploading(false);
      setSaving(false);
    }
  };

  /* ── delete ── */
  const handleDelete = async (id: string) => {
    await dispatch(deleteProduct(id));
    setConfirmDeleteId(null);
    setToast("Product deleted");
    if (view === "form" && editingId === id) navigate(`/store/${brandId}/products`);
  };

  const pricingSummary = `${fmtMoney(form.rate)} · ${form.stock} in stock${form.sku ? ` · ${form.sku}` : ""}`;

  const filledAttrCount = useMemo(
    () =>
      attrs.filter((a: any) => {
        const v = form.attributes[String(a.id)];
        return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "" && v !== null;
      }).length,
    [attrs, form.attributes]
  );

  const attrSummary = attrs.length
    ? `${filledAttrCount} of ${attrs.length} filled in`
    : "Pick a category first";

  /* ═══════════════════════════════════════════════════════════
     LIST VIEW
  ═══════════════════════════════════════════════════════════ */
  if (view === "list") {
    return (
      <div className="sp-root sp-fade">
        <style>{SP_CSS}</style>

        <div className="sp-topbar">
          <div className="sp-topbar-a">
            <div className="sp-topbar-tabs">
              {(["all", "active", "draft"] as const).map((k) => (
                <button key={k} className={`sp-topbar-tab${statusFilter === k ? " active" : ""}`} onClick={() => setStatusFilter(k)}>
                  {k === "all" ? "All" : k === "active" ? "Active" : "Draft"}
                  <span className="sp-num">{counts[k]}</span>
                </button>
              ))}
            </div>
            <button
              className="sp-btn sp-btn-primary"
              style={{ padding: "10px 18px", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
              onClick={openNewProduct}
            >
              <Plus size={15} /> Add product
            </button>
          </div>
          <div className="sp-topbar-b">
            <div className="sp-searchbar" style={{ flex: 1 }}>
              <Search size={14} color={HEART.textFaint} />
              <input placeholder="Search by name, SKU or category" value={search} onChange={(e) => setSearch(e.target.value)} />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{ background: HEART.border, border: "none", borderRadius: 999, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
        </div>

        {brandProductsLoading ? (
          <div className="sp-ptable" style={{ padding: 16 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="sp-skeleton" style={{ height: 54, borderRadius: 12, marginBottom: 10 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="sp-card sp-empty">
            <div style={{ width: 56, height: 56, borderRadius: 16, background: HEART.goldSoft, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Package size={24} color={HEART.goldDark} />
            </div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>
              {brandProducts.length === 0 ? "No products yet" : "No matches"}
            </p>
            <p style={{ fontSize: 13, color: HEART.textMuted, maxWidth: 330, margin: "0 0 18px" }}>
              {brandProducts.length === 0
                ? "Add your first product and start selling in your store."
                : "Change the search or switch tabs to see more."}
            </p>
            {brandProducts.length === 0 && (
              <button
                className="sp-btn sp-btn-primary"
                style={{ padding: "10px 20px", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
                onClick={openNewProduct}
              >
                <Plus size={15} /> Add product
              </button>
            )}
          </div>
        ) : (
          <div className="sp-ptable">
            <div className="sp-pt-scroll">
              <div className="sp-pt-grid">
                <div className="sp-pt-head">
                  <div
                    className={`sp-pcard-check${filtered.length > 0 && selectedIds.size === filtered.length ? " on" : ""}`}
                    onClick={toggleSelectAll}
                    title="Select all"
                  >
                    {filtered.length > 0 && selectedIds.size === filtered.length && <Check size={13} />}
                  </div>
                  <div className="sp-pt-hcell">Product</div>
                  <div className="sp-pt-hcell">Status</div>
                  <div className="sp-pt-hcell">Inventory</div>
                  <div className="sp-pt-hcell">Price</div>
                  <div className="sp-pt-hcell">Category</div>
                  <div className="sp-pt-hcell">Colours</div>
                  <div className="sp-pt-hcell">Vendor</div>
                  <div className="sp-pt-hcell">Updated</div>
                </div>

                {filtered.map((p: any) => {
                  const isSelected = selectedIds.has(p.listingId);
                  const vs: any[] = Object.values(p.variants || {});
                  const stock = vs.length
                    ? vs.reduce((sum, v) => sum + (Number(v?.stock) || 0), 0)
                    : p.stock ?? 0;
                  const oos = stock <= 0;
                  const fmtDate = (v: any) => {
                    if (!v) return "—";
                    const d = v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
                    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
                  };
                  const cover = vs.find((v) => v?.imageUrl)?.imageUrl || (p.photos && p.photos[0]);
                  return (
                    <div
                      key={p.listingId}
                      className={`sp-pt-row${isSelected ? " selected" : ""}`}
                      onClick={() => openEditProduct(p)}
                    >
                      <div className={`sp-pcard-check${isSelected ? " on" : ""}`} onClick={(e) => toggleSelect(p.listingId, e)}>
                        {isSelected && <Check size={13} />}
                      </div>

                      <div className="sp-pt-prod">
                        <div className="sp-pt-thumb">
                          {cover ? <img src={cover} alt={p.name} /> : <ImageIcon size={15} color={HEART.textFaint} />}
                        </div>
                        <span style={{ minWidth: 0 }}>
                          <span className="sp-pt-name" style={{ display: "block" }}>{p.name || "Untitled product"}</span>
                          {p.sku && <span className="sp-pt-sub">{p.sku}</span>}
                        </span>
                      </div>

                      <div>
                        <span className={`sp-pill ${oos ? "sp-pill-oos" : p.isPublished ? "sp-pill-active" : "sp-pill-draft"}`}>
                          {oos ? "Out of stock" : p.isPublished ? "Active" : "Draft"}
                        </span>
                      </div>

                      <div className={`sp-pt-cell${oos ? " oos" : ""}`}>
                        <span className="sp-num">{stock}</span>
                        <span style={{ color: HEART.textFaint, fontWeight: 500 }}>
                          {vs.length ? ` across ${vs.length}` : " in stock"}
                        </span>
                      </div>

                      <div className="sp-pt-cell">
                        <span className="sp-num">{fmtMoney(p.rate)}</span>
                        <span style={{ color: HEART.textFaint, fontWeight: 500 }}>
                          {" "}{RATE_LABEL[(p.rateType as RateType) || DEFAULT_RATE_TYPE]}
                        </span>
                      </div>

                      <div className="sp-pt-cell muted">
                        {p.categoryId ? pathLabel(String(p.categoryId)).split(" › ").slice(-2).join(" › ") : p.category || "Uncategorized"}
                      </div>

                      <div className="sp-pt-cell">
                        {vs.length ? (
                          <span className="sp-dots">
                            {vs.slice(0, 5).map((v: any, i: number) => (
                              <span key={i} className="sp-dot" style={{ background: v?.hex || HEART.panel2 }} title={v?.name} />
                            ))}
                            {vs.length > 5 && <span style={{ fontSize: 10.5, color: HEART.textFaint, fontWeight: 700 }}>+{vs.length - 5}</span>}
                          </span>
                        ) : (
                          <span className="sp-pt-cell muted">—</span>
                        )}
                      </div>

                      <div className="sp-pt-cell muted">{p.vendor || "—"}</div>
                      <div className="sp-pt-cell muted">{fmtDate(p.updatedAt)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="sp-bulkfloat sp-fade">
            <span style={{ fontSize: 12.5, fontWeight: 800, marginRight: 4 }}>
              <span style={{ color: HEART.gold }}>{selectedIds.size}</span> selected
            </span>
            <button className="sp-bulkfloat-btn" onClick={() => bulkSetPublished(true)}>Set active</button>
            <button className="sp-bulkfloat-btn" onClick={() => bulkSetPublished(false)}>Set draft</button>
            <button className="sp-bulkfloat-btn danger" onClick={bulkDelete}>Delete</button>
            <button className="sp-bulkfloat-btn" onClick={() => setSelectedIds(new Set())}>Clear</button>
          </div>
        )}

        {confirmDeleteId && (
          <>
            <div className="sp-sheet-backdrop" onClick={() => setConfirmDeleteId(null)} />
            <div className="sp-modal" style={{ textAlign: "center" }}>
              <h3 style={{ marginBottom: 8 }}>Delete this product?</h3>
              <p style={{ fontSize: 12.5, color: HEART.textMuted, margin: "0 0 20px" }}>
                It leaves your store straight away and can't be brought back.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="sp-btn sp-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setConfirmDeleteId(null)}>Keep it</button>
                <button className="sp-btn sp-btn-danger" style={{ flex: 1, padding: "11px 0" }} onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
              </div>
            </div>
          </>
        )}

        {toast && (
          <div className="sp-slide" style={{ position: "fixed", bottom: 24, right: 24, background: HEART.dark, color: "#fff", padding: "12px 18px", borderRadius: 12, fontSize: 13, fontWeight: 700, boxShadow: "0 12px 30px rgba(27,14,6,.3)", zIndex: 300 }}>
            {toast}
          </div>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     FORM VIEW
  ═══════════════════════════════════════════════════════════ */
  return (
    <div className="sp-root sp-fade" style={isMobile ? { paddingBottom: 96 } : undefined}>
      <style>{SP_CSS}</style>

      <div className="sp-formhead">
        <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <button
            className="sp-btn sp-btn-ghost"
            style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
            onClick={backToList}
            title="Back to products"
          >
            <ChevronLeft size={17} />
          </button>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {editingId ? form.name || "Edit product" : "Add product"}
            </h1>
            {editingId && (
              <p style={{ fontSize: 12, color: HEART.textFaint, margin: "2px 0 0" }}>
                <span className="sp-num">{fmtMoney(form.rate)}</span>
                {form.sku ? ` · ${form.sku}` : ""}
              </p>
            )}
          </div>
        </div>
        {!isMobile && (
          <div style={{ display: "flex", gap: 10 }}>
            <button className="sp-btn sp-btn-ghost" style={{ padding: "10px 18px", fontSize: 13 }} onClick={backToList} disabled={saving}>
              Discard
            </button>
            <button
              className="sp-btn sp-btn-primary"
              style={{ padding: "10px 22px", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <span className="sp-spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%" }} />}
              Save
            </button>
          </div>
        )}
      </div>

      <div className="sp-formgrid" style={{ display: "grid", gridTemplateColumns: "1fr 330px", gap: 20, alignItems: "start" }}>
        {/* ── MAIN COLUMN ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* details */}
          <div className="sp-card">
            <div className="sp-cardhead">
              <div className="sp-cardic hot"><Package size={15} /></div>
              <p className="sp-cardtitle" style={{ margin: 0 }}>Product details</p>
            </div>

            <label className="sp-label">Title</label>
            <input
              className="sp-input"
              placeholder="Short sleeve t-shirt"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />

            <hr className="sp-section-divider" />

            <label className="sp-label">Description</label>
            <RichTextEditor
              value={form.description}
              onChange={setDescription}
              resetKey={editingId}
              placeholder="Materials, fit, care instructions…"
            />

            <hr className="sp-section-divider" />

            <label className="sp-label">Media</label>
            {(form.photos.length > 0 || pendingPreviews.length > 0) && (
              <div className="sp-mediagrid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
                {form.photos.map((url, i) => {
                  const busy = removingPhoto === url;
                  return (
                    <div key={`url-${url}`} className="sp-thumb" style={busy ? { opacity: 0.45 } : undefined}>
                      <img src={url} alt="" />
                      {i === 0 && (
                        <span className="sp-thumb-tag" style={{ background: `linear-gradient(135deg,${HEART.red},${HEART.redDark})` }}>Cover</span>
                      )}
                      <button
                        className="sp-thumb-del"
                        disabled={busy}
                        style={busy ? { opacity: 1 } : undefined}
                        onClick={() => handleRemoveSavedPhoto(url)}
                        aria-label="Remove photo"
                      >
                        {busy ? (
                          <span className="sp-spin" style={{ width: 10, height: 10, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%" }} />
                        ) : (
                          <X size={12} />
                        )}
                      </button>
                    </div>
                  );
                })}

                {pendingPreviews.map((preview, i) => (
                  <div key={`file-${i}`} className="sp-thumb" style={{ opacity: 0.8 }}>
                    <img src={preview} alt="" />
                    <span className="sp-thumb-tag" style={{ background: "rgba(27,14,6,.7)" }}>Pending</span>
                    <button className="sp-thumb-del" onClick={() => handleRemovePendingPhoto(i)} aria-label="Remove photo">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              className={`sp-media-drop${dragging ? " dragging" : ""}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
            >
              {uploading ? (
                <>
                  <span className="sp-spin" style={{ width: 22, height: 22, border: `2.5px solid ${HEART.border}`, borderTopColor: HEART.red, borderRadius: "50%" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: HEART.textMuted }}>Uploading…</span>
                </>
              ) : (
                <>
                  <ImageIcon size={22} color={HEART.goldDark} />
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>Drop images here, or click to upload</span>
                  <span style={{ fontSize: 11, color: HEART.textFaint }}>First image becomes the cover. JPG, PNG or WEBP.</span>
                </>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />

            <hr className="sp-section-divider" />

            <label className="sp-label">Category</label>
            <CategoryPicker
              value={form.categoryId}
              onChange={(id, label) =>
                /* A new category means a new field set — old answers wouldn't fit. */
                setForm((f) => ({ ...f, categoryId: id, category: label, attributes: {}, variants: {} }))
              }
              isMobile={isMobile}
            />
            <p className="sp-hint">Drill in for a closer match — the fields below follow your choice.</p>
          </div>

          {/* attributes — card on desktop, tap-to-open sheet on mobile */}
          {attrs.length > 0 && (
            isMobile ? (
              <button className="sp-openrow" onClick={() => setAttrSheetOpen(true)}>
                <div className="sp-openrow-l">
                  <div className="sp-cardic"><SlidersHorizontal size={15} /></div>
                  <div style={{ minWidth: 0 }}>
                    <p className="sp-cardtitle" style={{ margin: 0 }}>{NODES.get(form.categoryId)?.name ?? "Category"} details</p>
                    <p className="sp-openrow-sum">{attrSummary}</p>
                  </div>
                </div>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {filledAttrCount > 0 && <span className="sp-badge">{filledAttrCount}</span>}
                  <ChevronDown size={16} style={{ transform: "rotate(-90deg)", color: HEART.textFaint }} />
                </span>
              </button>
            ) : (
              <div className="sp-card sp-fade">
                <div className="sp-cardhead">
                  <div className="sp-cardic"><Tag size={15} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="sp-cardtitle" style={{ margin: 0 }}>{NODES.get(form.categoryId)?.name ?? "Category"} details</p>
                    <p className="sp-hint" style={{ marginTop: 2 }}>{attrSummary}</p>
                  </div>
                </div>
                <AttributeFields
                  attrs={attrs}
                  values={form.attributes}
                  isMobile={isMobile}
                  colorAttrId={colorAttrId}
                  onChange={(attrId, value) => set("attributes", { ...form.attributes, [attrId]: value })}
                  onToggleColor={toggleColor}
                />
              </div>
            )
          )}

          {/* per-colour rows — only exist once a colour is picked */}
          {hasVariants && (
            <div className="sp-card sp-fade">
              <div className="sp-cardhead" style={{ marginBottom: 12 }}>
                <div className="sp-cardic"><Layers size={15} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="sp-cardtitle" style={{ margin: 0 }}>By colour</p>
                  <p className="sp-hint" style={{ marginTop: 2 }}>
                    A photo, quantity and price for each one. Prices start on the base price of {fmtMoney(form.rate)}.
                  </p>
                </div>
                <span className="sp-badge" style={{ height: 22, minWidth: 22 }}>{variantList.length}</span>
              </div>
              {variantList.map((v) => (
                <VariantCard
                  key={v.colorId}
                  variant={v}
                  baseRate={form.rate}
                  rateType={form.rateType}
                  onUpdate={updateVariant}
                  onRemove={toggleColor}
                />
              ))}
            </div>
          )}

          {/* pricing & inventory */}
          {isMobile ? (
            <button className="sp-openrow" onClick={() => setPricingSheetOpen(true)}>
              <div className="sp-openrow-l">
                <div className="sp-cardic"><Scale size={15} /></div>
                <div style={{ minWidth: 0 }}>
                  <p className="sp-cardtitle" style={{ margin: 0 }}>Pricing & inventory</p>
                  <p className="sp-openrow-sum">{pricingSummary}</p>
                </div>
              </div>
              <ChevronDown size={16} style={{ transform: "rotate(-90deg)", color: HEART.textFaint, flexShrink: 0 }} />
            </button>
          ) : (
            <div className="sp-card">
              <PricingInventoryFields form={form} hasVariants={hasVariants} variantCount={variantList.length} onGenerateSku={handleGenerateSku} set={set} />
            </div>
          )}

          {/* shipping */}
          <div className="sp-card">
            <div className="sp-cardhead">
              <div className="sp-cardic"><Truck size={15} /></div>
              <p className="sp-cardtitle" style={{ margin: 0 }}>Shipping</p>
            </div>

            <label className="sp-label">Product weight (kg)</label>
            <div style={{ maxWidth: 230, marginBottom: 16 }}>
              <NumberStepper value={form.weight} onChange={(n) => set("weight", n)} step={0.1} decimals suffix="kg" />
            </div>

            <label className="sp-label">Country of origin</label>
            <CountryPicker value={form.originCountry} onChange={(code) => set("originCountry", code)} isMobile={isMobile} />
            <p className="sp-hint">Used for customs paperwork on international orders.</p>
          </div>
        </div>

        {/* ── SIDEBAR ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div className="sp-card">
            <p className="sp-cardtitle">Status</p>
            <select
              className="sp-select"
              value={form.isPublished ? "active" : "draft"}
              onChange={(e) => set("isPublished", e.target.value === "active")}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
            <p className="sp-hint">
              {form.isPublished ? "Visible to customers in your store." : "Hidden from customers until you publish it."}
            </p>
          </div>

          <div className="sp-card">
            <p className="sp-cardtitle">Organization</p>
            <label className="sp-label">Vendor</label>
            <input
              className="sp-input"
              placeholder={brandName || "e.g. Heartlax Studio"}
              value={form.vendor}
              onChange={(e) => set("vendor", e.target.value)}
              style={{ marginBottom: brandName ? 6 : 14 }}
            />
            {brandName && (
              <p className="sp-hint" style={{ marginBottom: 14 }}>
                {form.vendor.trim() === brandName
                  ? "Your brand, filled in for you. Change it if someone else made this."
                  : `Leave blank to sell it as ${brandName}.`}
              </p>
            )}
            <label className="sp-label">Tags</label>
            <input
              className="sp-input"
              placeholder="Comma separated, e.g. summer, cotton"
              value={form.tags.join(", ")}
              onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
            />
            {form.tags.length > 0 && (
              <div className="sp-chips" style={{ marginTop: 12 }}>
                {form.tags.map((t) => (
                  <span key={t} className="sp-chip on">
                    {t}
                    <button className="sp-chip-del" onClick={() => set("tags", form.tags.filter((x) => x !== t))} aria-label={`Remove ${t}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* at a glance */}
          <div className="sp-card">
            <p className="sp-cardtitle">At a glance</p>
            {[
              {
                k: "Base price",
                v: form.rateType === "perItem" && hasVariants
                  ? "Set per colour"
                  : fmtRate(form.rate, form.rateType),
              },
              { k: "Charged", v: RATE_COPY[form.rateType].title },
              { k: "Stock", v: hasVariants ? `${variantList.reduce((s, v) => s + (v.stock || 0), 0)} across ${variantList.length}` : String(form.stock) },
              { k: "SKU", v: form.sku || "Auto on save" },
              { k: "Photos", v: String(form.photos.length + pendingPreviews.length) },
              { k: "Category", v: NODES.get(form.categoryId)?.name ?? "—" },
            ].map((row) => (
              <div key={row.k} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: `1px dashed ${HEART.divider}`, fontSize: 12 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: HEART.textFaint }}>{row.k}</span>
                <span style={{ fontWeight: 700, textAlign: "right", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{row.v}</span>
              </div>
            ))}
            {variantList.map((v) => (
              <div key={v.colorId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: `1px dashed ${HEART.divider}`, fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  {v.hex && <span className="sp-varswatch" style={{ background: v.hex }} />}
                  <span style={{ fontSize: 11, fontWeight: 700, color: HEART.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.name}</span>
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  <span className="sp-num">{v.stock}</span>
                  <span style={{ color: HEART.textFaint, fontWeight: 500 }}> · {fmtMoney(v.rate ?? form.rate)}</span>
                </span>
              </div>
            ))}
          </div>

          {editingId && (
            <button
              className="sp-btn sp-btn-danger"
              style={{ padding: "12px 0", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              onClick={() => setConfirmDeleteId(editingId)}
            >
              <Trash2 size={14} /> Delete product
            </button>
          )}
        </div>
      </div>

      {/* mobile attributes sheet */}
      {isMobile && attrSheetOpen && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setAttrSheetOpen(false)} />
          <div className="sp-sheet">
            <div className="sp-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
            <div className="sp-sheet-header">
              <p style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>
                {NODES.get(form.categoryId)?.name ?? "Category"} details
              </p>
              <button className="sp-btn sp-btn-primary" style={{ padding: "9px 18px", fontSize: 12.5 }} onClick={() => setAttrSheetOpen(false)}>
                Done
              </button>
            </div>
            <div className="sp-sheet-body">
              <AttributeFields
                attrs={attrs}
                values={form.attributes}
                isMobile={isMobile}
                colorAttrId={colorAttrId}
                onChange={(attrId, value) => set("attributes", { ...form.attributes, [attrId]: value })}
                onToggleColor={toggleColor}
              />
            </div>
          </div>
        </>
      )}

      {/* mobile pricing sheet */}
      {isMobile && pricingSheetOpen && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setPricingSheetOpen(false)} />
          <div className="sp-sheet">
            <div className="sp-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
            <div className="sp-sheet-header">
              <p style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Pricing & inventory</p>
              <button className="sp-btn sp-btn-primary" style={{ padding: "9px 18px", fontSize: 12.5 }} onClick={() => setPricingSheetOpen(false)}>
                Done
              </button>
            </div>
            <div className="sp-sheet-body">
              <PricingInventoryFields form={form} hasVariants={hasVariants} variantCount={variantList.length} onGenerateSku={handleGenerateSku} set={set} />
            </div>
          </div>
        </>
      )}

      {/* mobile sticky save */}
      {isMobile && (
        <div className="sp-sticky-save">
          <button className="sp-btn sp-btn-ghost" style={{ padding: "13px 20px", fontSize: 13 }} onClick={backToList} disabled={saving}>
            Discard
          </button>
          <button
            className="sp-btn sp-btn-primary"
            style={{ flex: 1, padding: "13px 0", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <span className="sp-spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%" }} />}
            {editingId ? "Update product" : "Save product"}
          </button>
        </div>
      )}

      {confirmDeleteId && (
        <>
          <div className="sp-sheet-backdrop" onClick={() => setConfirmDeleteId(null)} />
          <div className="sp-modal" style={{ textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Delete this product?</h3>
            <p style={{ fontSize: 12.5, color: HEART.textMuted, margin: "0 0 20px" }}>
              It leaves your store straight away and can't be brought back.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="sp-btn sp-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setConfirmDeleteId(null)}>Keep it</button>
              <button className="sp-btn sp-btn-danger" style={{ flex: 1, padding: "11px 0" }} onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
            </div>
          </div>
        </>
      )}

      {toast && (
        <div className="sp-slide" style={{ position: "fixed", bottom: isMobile ? 90 : 24, right: 24, left: isMobile ? 24 : undefined, textAlign: isMobile ? "center" : undefined, background: HEART.dark, color: "#fff", padding: "12px 18px", borderRadius: 12, fontSize: 13, fontWeight: 700, boxShadow: "0 12px 30px rgba(27,14,6,.3)", zIndex: 300 }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default StoreProducts;